import type {
  Channel,
  CourierMode,
  DeviceType,
  OrderPlatform,
  OrderStatus,
  PaymentMethod,
  SettleGroup,
  StaffRole,
} from "../api/types";

/** The name of an online platform: in full ("Takeaway.com") and short, in a list ("Takeaway"). */
const PLATFORM_NAMES: Record<OrderPlatform, { full: string; short: string }> = {
  mymenu: { full: "Mymenu.be", short: "Mymenu" },
  takeaway: { full: "Takeaway.com", short: "Takeaway" },
  ubereats: { full: "Uber Eats", short: "Uber Eats" },
};

/** Every text on the till, in Dutch. French and English follow the same shape. */
export const nl = {
  app: {
    till: "Kassa",
    /** Back from the full till to the Tafo app (ober, koerier) on the owner's phone (Furkan, 18/10/2026). */
    sale: "Verkoop",
    orders: "Bestellingen",
    couriers: "Koeriers",
    parked: "Wachtlijst",
    liveMap: "Live-kaart",
    /** The symbol for the look of the till: "Zwart" is on or off, and the hint says what a tap gives. */
    dark: "Zwart",
    toDark: "Zwart (nacht)",
    toLight: "Wit (dag)",
    /** How large the till is drawn on this device: the letter in the bar, its name and its hint. */
    size: {
      name: { s: "S", m: "M" },
      label: (size: string) => `Weergave ${size}`,
      hint: (size: string, percent: number, other: string, otherPercent: number) =>
        `Weergave ${size} (${String(percent)}%) · tik voor ${other} (${String(otherPercent)}%)`,
    },
    deviceHint: "Dit toestel wordt automatisch herkend",
  },
  /** De Tafo-app voor ober en koerier (prototype renderSetup, renderPhone, ob*; blok J). */
  tafoApp: {
    title: "Tafo-app",
    signOut: "Afmelden",
    roles: { waiter: "Ober", courier: "Koerier" },
    appNames: { waiter: "ober", courier: "koerier" },
    /** "Ober ⇄ Koerier" at the top for whoever may use both (Furkan, 09/10/2026). */
    mode: {
      label: "Werken als",
      now: (app: string) => `Je werkt nu als ${app}`,
      /** The owner: the full till of app.tafo.be on this phone, in the same app (Furkan, 18/10/2026). */
      till: "Kassa",
      tillOpening: "De kassa wordt geopend…",
    },
    pairing: {
      title: "Tafo-app koppelen",
      intro:
        "Vraag de eigenaar om een koppelcode (Beheer › Printers & toestellen › Toestellen › Nieuw toestel) en typ ze hieronder. Dit gebeurt één keer; daarna wordt dit toestel herkend.",
    },
    waitTerms: "Wacht op het akkoord van de eigenaar",
    waitTermsText:
      "De eigenaar moet eerst de algemene voorwaarden en de verwerkersovereenkomst (GDPR) aanvaarden, via de link in zijn mailbox of op de kassa. Daarna werkt de app meteen.",
    blocked: "Deze zaak is gepauzeerd",
    blockedText: "Neem contact op met Tafo. Daarna werkt de app meteen weer.",
    isTill: "Dit toestel is gekoppeld als kassa",
    isTillText: (url: string) => `Open de kassa op ${url}`,
    retry: "Opnieuw proberen",
    signIn: {
      whoAreYou: "Wie ben jij?",
      pickName: "Tik je naam aan",
      otherName: "‹ andere naam",
      nobody: "Nog niemand mag de Tafo-app gebruiken. De eigenaar zet dit aan bij Beheer › Personeel.",
      prompt: "Meld je aan met je persoonlijke code",
      signedIn: (name: string, app: string) => `${name} aangemeld in de ${app}-app`,
      homeScreen:
        "Zet de app op je beginscherm: op een iPhone in Safari via Delen › Zet op beginscherm, op Android in Chrome via ⋮ › Toevoegen aan startscherm.",
      ok: "OK",
    },
    /** After Afmelden (Furkan, 10/10/2026). */
    signedOut: {
      title: "Je bent afgemeld",
      bye: (name: string) => `Tot later, ${name}.`,
      again: "Aanmelden",
    },
    tabs: {
      home: "Bestellingen",
      tables: "Tafels",
      takeaway: "Afhaal",
      ready: "Te bezorgen",
      rides: "Mijn ritten",
      cash: "Mijn saldo",
    },
    /** Start of the waiter's app (Furkan, 08/10/2026): the orders of today, another payment method. */
    home: {
      listLabel: "Bestellingen van vandaag",
      listTitle: "Bestellingen",
      filterLabel: "Toon",
      filters: { all: "Alle", open: "Open", paid: "Betaald" },
      none: {
        all: "Nog geen bestellingen vandaag.",
        open: "Geen open bestellingen.",
        paid: "Nog niets betaald vandaag.",
      },
      table: (table: string) => `Tafel ${table}`,
      takeaway: "Afhaal",
      takeawayFor: (name: string) => `Afhaal · ${name}`,
      delivery: "Levering",
      open: "open",
      cancelled: "geannuleerd",
      openTable: (table: string) => `Tafel ${table} openen`,
      methodNote:
        "Bv. de klant betaalt toch met Bancontact in plaats van contant. Elke wijziging wordt bewaard en staat in het Z-rapport.",
      methodNeedsCode:
        "Een andere betaalwijze achteraf vraagt de code van een manager of de eigenaar. Elke wijziging wordt bewaard en staat in het Z-rapport.",
    },
    waiter: {
      tablesNote:
        "Oranje = bezet, een stippellijn = nog niet naar de keuken. Tik een tafel om te bestellen, bij te bestellen, de rekening af te drukken of af te rekenen.",
      busyList: "Bezet",
      free: "vrij",
      notSent: "niet verstuurd",
      busy: (minutes: number, amount: string) => `${String(minutes)} min · ${amount}`,
      backTables: "‹ tafels",
      back: "‹ terug",
      allTables: "‹ alle tafels",
      backCategories: "‹ categorieën",
      backTable: (table: string) => `‹ tafel ${table}`,
      table: (table: string) => `Tafel ${table}`,
      since: (time: string) => `sinds ${time}`,
      takeaway: "Afhaal",
      move: "Tafel verplaatsen",
      /** As on the till: phone, name, e-mail, the note on the receipt (Furkan, 19/10/2026). */
      takeawayDetails: "Afhaalgegevens",
      takeawayNote: "Opmerking (komt op de bon)",
      sent: "Al in de keuken",
      draft: "Nieuw (nog niet verstuurd)",
      remove: "Verwijderen",
      tableFree: "Tafel is vrij.",
      nothingYet: "Nog niets besteld.",
      ipadHint: "Kies rechts een categorie en tik op een product.",
      start: "Start bestelling",
      addProduct: "+ Product",
      total: "Totaal",
      toKitchen: (count: number) => `Naar keuken (${String(count)})`,
      bill: "Rekening",
      pay: "Afrekenen",
      later: "Betalen bij afhalen",
      payNow: "Nu afrekenen",
      search: "Zoek product of code…",
      allergens: "Allergenen",
      categories: "Categorieën",
      productCount: (count: number) => (count === 1 ? "1 product" : `${String(count)} producten`),
      soldOut: "uitverkocht",
      nothingFound: "Niets gevonden.",
      size: "Formaat",
      required: "verplicht",
      freePicks: (count: number) => `${String(count)} gratis`,
      freeLabel: "gratis",
      gone: "weg",
      kitchenNote: "Opmerking voor keuken",
      add: (amount: string) => `Toevoegen · ${amount}`,
      chooseFirst: (group: string) => `Kies eerst: ${group}`,
      added: (name: string) => `${name} toegevoegd`,
      moveTitle: (table: string) => `Tafel ${table} verplaatsen naar…`,
      moveNote: "Alles gaat mee. De keuken krijgt een melding.",
      noFreeTable: "Geen vrije tafel.",
      less: "Eén minder",
      more: "Eén meer",
      quantity: "Aantal",
      // the kitchen and the printers (prototype obSendKitchen, obPrn)
      kitchen: (table: string, extra: boolean, printers: string) =>
        `Tafel ${table}: ${extra ? "BIJBESTELLING" : "keukenbon"} → ${printers}`,
      kitchenNoPrinter: "keuken",
      viaPc: (pc: string | null) => (pc === null ? "via hoofdcomputer" : `via hoofdcomputer (${pc})`),
      viaShort: (pc: string | null) => `via ${pc ?? "hoofdcomputer"}`,
      billPrinted: (where: string) => `Rekening → ${where}`,
      billShown: "Rekening getoond · geen kassaprinter ingesteld voor tafels",
      noPrinter: "geen printer",
    },
    bill: {
      title: (table: string) => `Rekening tafel ${table}`,
      heading: (table: string) => `VOORLOPIGE REKENING · TAFEL ${table}`,
      kind: "Soort bestelling",
      kindValue: (table: string) => `Tafel · tafel ${table}`,
      date: "Datum",
      time: "Tijd",
      since: "Aan tafel sinds",
      waiter: "Ober",
      details: "Details bestelling",
      notReceipt: "Dit is geen kasticket.",
      payWaiter: "Afrekenen bij de ober.",
      /** The verdeling at the bottom (Furkan, 09/10/2026). */
      split: "VERDELING",
      person: (number: number, of: number) => `Persoon ${String(number)}/${String(of)}`,
      guest: (number: number) => `Gast ${String(number)}`,
      sentTo: (pc: string | null, printer: string) =>
        `Dit toestel stuurt de opdracht naar de hoofdcomputer${pc === null ? "" : ` (${pc})`}, die ze afdrukt op ${printer} met de printerinstellingen van de zaak.`,
      noPrinter:
        "Er is nog geen kassaprinter ingesteld voor tafels (Beheer › Printers & toestellen): toon de rekening op het scherm.",
    },
    /** The bank-QR of Bancontact Pro that confirms itself (ADR 0024). */
    bankQr: {
      making: "De QR wordt gemaakt…",
      waiting: "Wacht op de klant · de betaling komt hier vanzelf binnen",
      scanned: "Gescand · de klant bevestigt in zijn app…",
      left: (time: string) => `nog ${time}`,
      paid: (amount: string) => `${amount} betaald`,
      booking: "De betaling wordt geboekt…",
      failed: "De bank van de klant weigerde de betaling. Er is niets betaald.",
      expired: "De QR is verlopen. Er is niets betaald.",
      cancelled: "De QR is ingetrokken. Er is niets betaald.",
      again: "Nieuwe QR",
      retry: "Opnieuw proberen",
      other: "Andere betaalwijze",
      behind:
        "Bancontact antwoordt even niet. Toont de gsm van de klant “betaald”, wacht dan hier: neem geen tweede betaling aan.",
      notBooked:
        "De betaling is binnen bij Bancontact, maar boeken lukte niet. Neem geen tweede betaling aan en meld dit aan de zaakvoerder.",
    },
    pay: {
      title: (table: string | null) => (table === null ? "Afrekenen afhaal" : `Afrekenen tafel ${table}`),
      modes: { all: "Alles", items: "Per product", equal: "Gelijk verdelen" },
      itemsHint: "Tik wat deze persoon betaalt.",
      perPiece: (price: string, count: number) => `${price} per stuk · ${String(count)} besteld`,
      persons: "Aantal personen",
      personOf: (person: number, persons: number, share: string) =>
        `Persoon ${String(person)} van ${String(persons)} · elk ${share}`,
      total: "Totaal",
      nowDue: "Nu te betalen",
      tip: "Fooi",
      tipNone: "Geen",
      tipLine: (percent: number) => `Fooi ${String(percent)}%`,
      toPay: "Te betalen",
      methods: {
        cash: "Contant",
        qr: "QR op scherm",
        qrBill: "QR op de bon",
        tap: "Tap to Pay",
        sunmi: "Kaart (Sunmi)",
        terminal: "Kaart (terminal)",
      },
      cashDone: "Contant ontvangen · bon afdrukken",
      qrScan: "Scan met je bank-app",
      qrCheck: "Kijk op de gsm van de klant of de betaling gelukt is.",
      qrDone: "Betaling ontvangen · bon afdrukken",
      qrFound: (amount: string) => `De bank-QR van ${amount} was al betaald: nu geboekt`,
      terminalType: (amount: string) => `Tik ${amount} in op de losse terminal.`,
      terminalDone: "Betaald op de terminal · bon afdrukken",
      itemsWithDiscount: "Per product kan niet bij een rekening met korting.",
      itemsWhileSplit: "Deze tafel wordt al gelijk verdeeld.",
      tipPart: (amount: string) => ` + fooi ${amount}`,
      paidAll: (table: string, amount: string, tip: string, where: string) =>
        `Tafel ${table} betaald (${amount})${tip} · kassabon ${where}`,
      paidPart: (table: string, amount: string, tip: string, where: string) =>
        `Deel van tafel ${table} betaald (${amount})${tip} · bon ${where}`,
      personPaid: (person: number, persons: number, amount: string) =>
        `Persoon ${String(person)} van ${String(persons)} betaald (${amount})`,
      paidEqual: (table: string, persons: number, tip: string, where: string) =>
        `Tafel ${table} volledig betaald (${String(persons)} personen)${tip} · bon ${where}`,
      takeawayPaid: (code: string, kitchen: string, where: string) =>
        `Afhaal ${code} betaald · ${kitchen} + kassabon · ${where}`,
      takeawayOpen: (code: string) => `Afhaal ${code} staat open: reken de rest af aan de kassa (Wachtlijst)`,
      kitchenTicket: "keukenbon",
      kitchenCopy: "kopie afrekening in de keuken",
      takeawayLater: (name: string, copy: boolean) =>
        `Afhaal${name === "" ? "" : ` ${name}`} naar keuken${copy ? " (kopie van de afrekening, nog te betalen)" : ""} · staat in de wachtlijst op de kassa om af te rekenen`,
      reference: (what: string) => `Tafo ${what}`,
      methodsLabel: "Betaalwijze",
      deferred: "De app verstuurt deze betaling zodra de server bereikbaar is. Niet opnieuw aanrekenen.",
      /** Bancontact Pro QR on the screen or on the bill (Furkan, 09/10/2026). */
      qrBillAll: "Rekening met QR afdrukken",
      qrBillEqual: (persons: number) => `Rekening met een QR per persoon (${String(persons)})`,
      qrBillGuest: "Op de rekening met eigen QR",
      qrBillTakeaway: "Bon met QR afdrukken",
      qrBillNoTip: "De QR op de bon vraagt het bedrag zonder fooi.",
      qrBillHint: "De klant scant wanneer hij wil. Tafo zet het vanzelf op betaald zodra Bancontact het bevestigt.",
      takeawayQr: (code: string, amount: string) =>
        `Afhaal ${code}: bon met Bancontact Pro QR gedrukt (${amount}). Tafo zet ze vanzelf op betaald zodra de klant scant.`,
      /** The verdeling at the bottom of the bill. */
      split: {
        hint: "Tik wat een gast nam en zet hem op de rekening. Onderaan de rekening staat dan per gast wat hij nam, zijn totaal en zijn eigen QR als hij die vraagt.",
        addGuest: (number: number) => `＋ Gast ${String(number)} op de rekening`,
        guest: (number: number) => `Gast ${String(number)}`,
        qr: "QR",
        qrTitle: "Eigen QR op de rekening",
        payNow: "Nu afrekenen",
        remove: (number: number) => `Gast ${String(number)} van de rekening halen`,
        gone: "staat niet meer op de tafel",
        paid: "✓ betaald met QR",
        print: "🧾 Rekening met verdeling",
        printEqual: "🧾 Rekening per persoon",
      },
    },
    courier: {
      readySince: (code: string, time: string) => `${code} · klaar sinds ${time}`,
      km: (km: string) => `± ${km} km`,
      paid: (method: string) => `Betaald · ${method}`,
      due: (amount: string) => `Te innen ${amount}`,
      take: (count: number) => (count === 0 ? "Neem ritten" : `Neem ritten (${String(count)})`),
      noneReady: "Geen bestellingen klaar. Je krijgt een melding zodra er één klaar is.",
      taken: (name: string, count: number) => `${name} neemt ${String(count)} ${count === 1 ? "rit" : "ritten"}`,
      readyToLeave: "Klaar om te vertrekken",
      depart: (count: number) => `Vertrek (${String(count)}) · route starten`,
      departNote: (count: number) =>
        `Bij vertrek berekent Tafo één snelste route langs álle ${String(count)} adressen samen, zonder heen en weer te rijden.`,
      onTheWay: "Onderweg · in deze volgorde",
      /** The courier's own position during a ride: what the app tells him about it. */
      sharing: {
        looking: "Je plaats wordt gezocht…",
        on: "Je plaats wordt gedeeld met de zaak en de klant, enkel tijdens deze rit. Hou Tafo open op je scherm.",
        denied:
          "Je plaats delen staat uit. De klant ziet dan een schatting. Zet “Locatie” aan voor Tafo in de instellingen van je gsm.",
        unavailable: "Deze gsm geeft geen plaats door. De klant ziet een schatting.",
      },
      /** In the app from the stores the position goes on with the screen off or Google Maps in front. */
      sharingNative:
        "Je plaats wordt gedeeld met de zaak en de klant, enkel tijdens deze rit. Dat blijft zo als je een kaart-app opent of je scherm uitgaat.",
      /** The notification of Android while a ride is on the way (the position goes on in the background). */
      background: {
        title: "Tafo · rit onderweg",
        message: "Je plaats wordt gedeeld tot je terug in de zaak bent.",
      },
      /** Everything delivered: the ride ends at the shop, so the shop sees when he is back (Furkan, 18/10/2026). */
      returning: "Terug naar de zaak",
      returningNote:
        "Je plaats wordt gedeeld met de zaak tot je terug bent, zodat ze je volgende bestellingen klaarmaken.",
      returningOff:
        "Je plaats delen staat uit: de zaak ziet niet wanneer je terug bent. Zet “Locatie” aan voor Tafo in de instellingen van je gsm.",
      backAtShop: "Ik ben terug in de zaak",
      fullRoute: (count: number) => (count === 1 ? "Navigeer" : `Volledige route (${String(count)} adressen)`),
      routeNote:
        "Tafo rekende één snelste route langs alle adressen samen. Google Maps en Apple Kaarten rijden ze in deze volgorde af.",
      arrival: (name: string, time: string) => (name === "" ? `aankomst ± ${time}` : `${name} · aankomst ± ${time}`),
      call: "Bel",
      navigate: "Navigeer",
      /** Two maps side by side (Furkan, 18/10/2026). */
      google: "Google Maps",
      apple: "Apple Kaarten",
      problemLabel: (code: string) => `Probleem melden bij ${code}`,
      noteLabel: (code: string) => `Opmerking bij ${code} lezen`,
      customerNote: "Opmerking van de klant",
      delivered: "Geleverd",
      noRidesSelf: "Nog geen ritten. Neem er één in Te bezorgen.",
      noRidesTill: "Nog geen ritten toegewezen door de kassa.",
      departed: (name: string, count: number, km: string) =>
        `${name} vertrekt met ${String(count)} ${count > 1 ? "ritten" : "rit"} · snelste route ± ${km} km · klanten krijgen mail met live volgen`,
      problemQuestion: "Wat is er aan de hand? De kassa krijgt meteen een melding.",
      /** The window of ⚠ (Furkan, 18/10/2026). */
      problemAsk: "Klant niet thuis, adres niet gevonden of weigert de klant de bestelling?",
      callShop: "Bel naar de zaak",
      noShopPhone: "Het telefoonnummer van de zaak staat niet in Beheer › Algemeen.",
      problemSent: (code: string, reason: string) => `Melding naar kassa: ${code} · ${reason}`,
      nothingToCollect: "Niets te innen. Gewoon afgeven.",
      handedOver: "Afgeleverd",
      toCollect: "Te innen",
      cashReceived: "Contant ontvangen · afgeleverd",
      customerQr: "Klant betaalt met bank-QR",
      onlineNote: 'Online betaald (webshop, later) staat hier als "Betaald": dan is er niets te innen.',
      qrScan: "Laat de klant scannen met zijn bank-app",
      qrCheck: "Kijk op de gsm van de klant of de betaling gelukt is.",
      qrDone: "Betaling ontvangen · afgeleverd",
      cashAfterAll: "toch contant",
      deliveredToast: (code: string, how: string | null) => `${code} geleverd${how === null ? "" : ` · ${how}`}`,
      cashHow: (amount: string) => `contant ${amount}`,
      qrHow: (amount: string) => `bank-QR ${amount}`,
      deliveredUnsettled: "Geleverd (nog niet afgerekend)",
      cashToHand: "Contant af te geven",
      qrReceived: "Bank-QR ontvangen",
      cashNote:
        "Afrekenen gebeurt aan de kassa: geef het contante geld af, een manager of de eigenaar telt en bevestigt.",
      problemOnBoard: (reason: string, time: string) => `${reason} · ${time}`,
      /** "Rit terugzetten": a ride taken by mistake goes back, until it is delivered (Furkan, 08/10/2026). */
      release: "Terugzetten",
      releaseLabel: (code: string) => `Rit ${code} terugzetten`,
      releaseQuestion: (code: string) => `Rit ${code} terugzetten?`,
      releaseWhereSelf: "Ze komt terug bij Te bezorgen: jij of een andere koerier kan ze opnieuw nemen.",
      releaseWhereTill: "Ze komt terug bij de kassa onder Te leveren: de kassa wijst ze opnieuw toe.",
      releaseOnTheWay:
        "Je bent al vertrokken met deze rit: de klant ziet je niet meer onderweg. Breng de bestelling terug naar de zaak.",
      releaseYes: "Ja, rit terugzetten",
      releaseNo: "Nee, ik hou ze",
      released: (code: string) => `${code} teruggezet`,
    },
  },
  beheer: {
    title: "Beheer",
    close: "✕ Beheer sluiten",
    sectionsOf: (name: string) => `Onderdelen van Beheer, nu ${name}`,
    signedIn: (name: string, role: string) => `Aangemeld als ${name} (${role}).`,
    ownerNote: "Dezelfde instellingen staan ook in het eigenaarsportaal op gsm en pc.",
    otherNote: "Je ziet enkel de onderdelen waarvoor je rechten hebt.",
    nothingYet: "De onderdelen voor jouw rechten volgen in de volgende stap.",
    sections: {
      menu: "Menu & prijzen",
      soldOut: "Uitverkocht",
      promotions: "Acties",
      staff: "Personeel & rechten",
      printers: "Printers & toestellen",
      floor: "Tafelplan",
      zones: "Leveringszones",
      customers: "Klanten",
      reports: "Rapporten & Z",
      time: "Tijdsregistratie",
      orders: "Bestellingen",
      subscription: "Abonnement & facturen",
      general: "Algemeen",
    },
  },
  confirm: { title: "Bevestigen", cancel: "Nee" },
  menuAdmin: {
    title: "Menu & prijzen",
    cancel: "Annuleren",
    tabs: { products: "Producten", groups: "Keuzegroepen", allergens: "Allergenen", folder: "Folder" },
    note: "Wijzigingen zijn meteen actief op alle toestellen. Een nieuw menu via foto/PDF gebeurt door Tafo.",
    categories: "Categorieën",
    newCategory: "Nieuwe categorie",
    up: "Categorie omhoog",
    down: "Categorie omlaag",
    /** The order of the products of a category: held and slid, or the alphabet (Furkan, 03/10/2026). */
    order: {
      label: "Volgorde van de producten",
      alphabetical: "Alfabetisch",
      own: "Eigen volgorde",
      hint: "Hou een product vast en schuif het op zijn plaats.",
      grip: (name: string) => `${name} verplaatsen`,
      gripHint: "Vasthouden en schuiven, of de pijltjestoetsen",
      moved: (name: string, place: number, count: number) =>
        `${name} staat nu op plaats ${String(place)} van ${String(count)} · meteen actief`,
      nowOwn: (category: string) => `${category}: eigen volgorde · een nieuw product komt achteraan`,
      nowAlphabetical: (category: string) => `${category} staat weer alfabetisch`,
      confirmAlphabetical: (category: string) =>
        `De eigen volgorde van ${category} vervalt: de producten staan dan weer alfabetisch.`,
      toAlphabetical: "Alfabetisch zetten",
      confirmOwn: (category: string) =>
        `${category} krijgt een eigen volgorde: je schuift de producten zelf op hun plaats. Een nieuw product komt achteraan.`,
      toOwn: "Eigen volgorde",
      // what the logbook of the shop says
      logMoved: (category: string, name: string, place: number) =>
        `Volgorde ${category}: ${name} naar plaats ${String(place)}`,
      logOwn: (category: string) => `Volgorde ${category}: eigen volgorde`,
      logAlphabetical: (category: string) => `Volgorde ${category}: alfabetisch`,
    },
    // prices straight in the list (Furkan, 09/10/2026)
    prices: {
      edit: (name: string, size: string) => `Prijs van ${name}${size ? ` ${size}` : ""}`,
      hint: "Klik op de prijs om ze te wijzigen",
      keys: "Enter: bewaren en naar de volgende prijs · Shift+Enter: de vorige · Esc: niet wijzigen",
      wrong: "Geen geldige prijs: bv. 14,50",
      // what the logbook of the shop says
      log: (changed: string[]) => `Prijs ${changed.join("; ")}`,
      notSaved: (changed: string[]) => `Niet bewaard: ${changed.join("; ")}. Probeer opnieuw.`,
    },
    editCategory: "Categorie aanpassen",
    vatLine: (takeaway: number, onSite: number, web: boolean, till: boolean) =>
      `BTW ${String(takeaway)}% afhaal/levering · ${String(onSite)}% tafel${web ? " · website: per formaat" : ""}${till ? " · kassa: per formaat" : ""}`,
    search: "Zoek product of code…",
    results: (count: number) => `Zoekresultaten (${String(count)})`,
    noProducts: "Geen producten",
    addProduct: (category: string) => `+ Product in ${category}`,
    soldOut: "uitverkocht",
    emptyTitle: "Kies links een product",
    emptyText:
      "De instellingen (naam, code, prijs of formaten, ingrediënten, keuzegroepen, uitverkocht) verschijnen hier.",
    saving: "Bewaren…",
    changed:
      "Het menu werd intussen op een ander toestel gewijzigd. Het menu is opnieuw geladen; doe je wijziging nog eens.",
    category: {
      new: "Nieuwe categorie",
      edit: "Categorie aanpassen",
      name: "Naam",
      namePlaceholder: "bv. Wraps",
      color: "Kleur van de tegels",
      colorGroups: { neon: "Neon", dull: "Dof" },
      colorLabel: (color: string) => `Kleur ${color}`,
      sizesTitle: "Producten met formaten tonen",
      web: "Op de website als aparte categorieën",
      webHint: (name: string) => `(bv. ${name || "Pizza"} small / medium / large)`,
      till: "Op de kassa per formaat gegroepeerd",
      tillHint: "(tegel = product + formaat, één tik)",
      sizesNote: "De producten en prijzen blijven één keer ingegeven; dit verandert enkel de weergave.",
      vatTitle: "BTW voor deze categorie",
      vatTakeaway: "Afhaal en levering",
      vatOnSite: "Ter plaatse (tafel)",
      quick: "Snel kiezen",
      food: "🍕 Eten",
      drink: "🥤 Frisdrank",
      alcohol: "🍺 Alcohol",
      vatNote:
        "België: eten om mee te nemen of te leveren 6%, eten ter plaatse 12%; frisdrank en water mee 6%, ter plaatse 21%; alcohol altijd 21%. Laat je boekhouder dit bevestigen. Een alcoholische drank in een andere categorie markeer je bij het product.",
      deleteOnlyEmpty: "Verwijderen kan pas als de categorie leeg is.",
      needName: "Geef de categorie een naam",
      added: (name: string) => `Categorie ${name} toegevoegd`,
      saved: "Categorie aangepast",
      deleted: "Categorie verwijderd",
    },
    product: {
      new: "Nieuw product",
      name: "Naam",
      namePlaceholder: "bv. Durum Kipfilet",
      code: "Code",
      codeHint: "voor snelle invoer",
      codePlaceholder: "bv. DKF",
      category: "Categorie",
      receiptName: "Naam op de bon",
      receiptHint: "kort, voor kassabon, rekening en keukenbon · leeg = gewone naam",
      receiptPlaceholder: "bv. Basis",
      onReceipt: "Op de bon:",
      description: "Omschrijving",
      descriptionHint: "voor de website, niet op de kassa",
      descriptionPlaceholder: "bv. met pitavlees, sla, tomaat en saus naar keuze",
      price: "Prijs",
      onePrice: "Eén prijs",
      sizes: "Formaten",
      priceMode: "Eén prijs of formaten",
      sizeLabel: (index: number) => `Formaat ${String(index)}`,
      priceOf: (size: string) => `Prijs ${size}`,
      removeSize: "Formaat verwijderen",
      addSize: "+ Formaat",
      newSize: "Nieuw",
      priceNote: "Eén kassaprijs voor afhaal, levering en tafel. De BTW volgt automatisch uit het kanaal.",
      ingredients: "Ingrediënten",
      ingredientsHint: 'hieruit komt de knop "Zonder"',
      addIngredient: "+ ingrediënt, Enter",
      removeIngredient: (name: string) => `${name} verwijderen`,
      groups: "Keuzegroepen",
      groupsHint: "gedeelde groepen, beheerd onder Keuzegroepen",
      link: (title: string) => `${title} koppelen`,
      options: (count: number) => `${String(count)} opties`,
      free: "gratis",
      freeOf: (title: string) => `Aantal gratis ${title}`,
      alcohol: "Alcohol",
      alcoholHint: "altijd 21% BTW, ook bij afhaal",
      categoryVat: (takeaway: number, onSite: number) =>
        `BTW van de categorie: ${String(takeaway)}% afhaal/levering · ${String(onSite)}% tafel`,
      special: "Specialiteit",
      specialHint: "vooraan in de categorie zolang die alfabetisch staat (bv. Stel zelf je pizza samen)",
      soldOut: "Uitverkocht",
      yes: "Ja",
      no: "Nee",
      delete: "Verwijderen",
      duplicate: "Dupliceren",
      close: "Sluiten",
      save: "Opslaan",
      confirmDelete: (name: string) => `"${name}" verwijderen uit het menu? Oude bestellingen blijven bewaard.`,
      saved: (name: string) => `${name} opgeslagen · meteen actief`,
      added: (name: string) => `${name} toegevoegd`,
      deleted: (name: string) => `${name} verwijderd`,
      copied: "Kopie gemaakt, pas naam en code aan",
    },
    allergens: {
      title: "Allergenen",
      take: "Voorstel overnemen",
      taken: "Voorstel overgenomen · opslaan en daarna bevestigen",
      confirmedBy: (by: string, at: string) => `Bevestigd door ${by} op ${at}`,
      differs: " · gewijzigd t.o.v. voorstel",
      changed: "Gewijzigd: na opslaan opnieuw te bevestigen in het tabblad Allergenen.",
      manual: "Manueel aangepast, nog niet bevestigd. Bevestig in het tabblad Allergenen.",
      proposal: "Voorstel van Tafo, nog niet bevestigd. Bevestig in het tabblad Allergenen.",
      legendHint: "Omrand = voorstel van Tafo; houd de muis op een allergeen voor de reden.",
      choices: "Keuzes (sauzen/extra's) kunnen extra allergenen toevoegen:",
      noChoices: "geen keuzes met allergenen gekoppeld",
      proposalTitle: (why: string) => `Voorstel:
${why}`,
      count: (ok: number, total: number) => `${String(ok)} van ${String(total)} producten bevestigd`,
      onlyOpen: "Enkel niet bevestigd",
      certain: "zeker",
      maybe: "mogelijk",
      tabNote:
        "Tafo stelt de allergenen voor op basis van de categorie, de ingrediënten en de naam. Keuzes (sauzen/extra's) kunnen extra allergenen toevoegen; die ziet u per product in de producteditor.",
      inCategory: (ok: number, total: number) => `${String(ok)} van ${String(total)} bevestigd`,
      confirmCategory: "Alles in deze categorie bevestigen",
      /** Every product not confirmed yet, over all categories (Furkan, 26/10/2026). */
      confirmAll: (count: number) => `Alles bevestigen (${String(count)})`,
      columns: { product: "Product", proposal: "Voorstel", confirmed: "Bevestigd", status: "Status" },
      none: "geen",
      notYet: "manueel, nog niet bevestigd",
      okOn: (at: string) => `Bevestigd ${at}`,
      diff: "Gewijzigd t.o.v. voorstel",
      diffTitle: (at: string, by: string) => `Bevestigd op ${at} door ${by}`,
      open: "Niet bevestigd",
      confirm: "Bevestigen",
      view: "Bekijken",
      allDone: "Alle producten zijn bevestigd.",
      confirmedOne: (name: string) => `Allergenen van ${name} bevestigd`,
      confirmedMany: (count: number) => `${String(count)} producten bevestigd`,
    },
    groups: {
      add: "+ Keuzegroep",
      note: "Een groep maak je één keer en koppel je aan veel producten. Aanpassen = overal meteen aangepast.",
      options: (count: number) => `${String(count)} opties`,
      free: (count: number) => ` · ${String(count)} gratis`,
      choose: (minimum: number, maximum: number) => ` · kies ${String(minimum)}${maximum ? `–${String(maximum)}` : ""}`,
      most: (maximum: number) => ` · max ${String(maximum)}`,
      bySize: " · prijs per formaat",
      usedBy: (count: number) => `Gebruikt door ${String(count)} product${count === 1 ? "" : "en"}`,
      // where it is linked (Furkan, 10/10/2026: "aan welke categorieën en of producten")
      linkedTo: "Gekoppeld aan",
      linkedNone: "Nog aan geen enkel product gekoppeld",
      whole: (count: number) => `alle ${String(count)}`,
      wholeLong: (count: number) => `de hele categorie (${String(count)} producten)`,
      some: (names: string[], more: number) => `${names.join(", ")}${more > 0 ? ` +${String(more)}` : ""}`,
      someOf: (count: number, of: number) => `${String(count)} van ${String(of)}`,
      moreCategories: (count: number) => `+ ${String(count)} categorie${count === 1 ? "" : "ën"}`,
      new: "Nieuwe keuzegroep",
      edit: "Keuzegroep aanpassen",
      name: "Naam",
      namePlaceholder: "bv. Sauzen",
      kind: "Soort",
      kinds: { extra: "Extra's / sauzen", choice: "Verplichte keuze", instruction: "Instructie" },
      minimum: "Minimum kiezen",
      maximum: "Maximum",
      maximumHint: "0 = onbeperkt",
      free_: "Aantal gratis",
      freeHint: "standaard, per product aanpasbaar",
      optionsTitle: "Opties",
      onePrice: "Eén prijs",
      bySizeLabel: (sizes: string) => `Prijs per formaat (${sizes})`,
      instructionsFree: "instructies zijn gratis",
      optionName: "Naam optie",
      optionNameOf: (index: number) => `Naam optie ${String(index)}`,
      channelsOf: (index: number) => `Bij welke bestellingen, optie ${String(index)}`,
      channelsHint: "Bij welke bestellingen deze optie getoond wordt, bv. Gesneden enkel aan tafel",
      channels: {
        "": "alle bestellingen",
        table: "enkel tafel",
        "takeaway,delivery": "niet aan tafel",
        takeaway: "enkel afhaal",
        delivery: "enkel levering",
        "takeaway,table": "niet bij levering",
        "delivery,table": "niet bij afhaal",
      } as Record<string, string>,
      fixedName: "naam aanpassen bij het product zelf",
      price: "Prijs",
      priceOf: (size: string) => `Prijs ${size}`,
      up: "Omhoog",
      remove: "Verwijderen",
      addOption: "+ Optie",
      usedFooter: (count: number) => `Gebruikt door ${String(count)} producten · wijzigingen gelden overal`,
      confirmDelete: (title: string, count: number) =>
        `Keuzegroep "${title}" verwijderen? Ze wordt losgekoppeld van ${String(count)} producten.`,
      saved: (title: string, count: number) => `${title} opgeslagen · aangepast bij ${String(count)} producten`,
      added: (title: string) => `${title} aangemaakt · koppel ze bij de producten`,
      deleted: "Keuzegroep verwijderd",
    },
    labels: {
      extra: "Extra's",
      choice: "Verplichte keuze",
      instruction: "Instructie",
      freeChoice: "Keuze met gratis",
    },
  },
  soldOut: {
    title: "Uitverkocht",
    note: "Tik een product om het uitverkocht te zetten. Het verdwijnt meteen op alle toestellen (en later ook op de website).",
    now: "Nu uitverkocht",
    nothing: "Niets uitverkocht.",
    search: "Zoek product…",
    mark: "Uitverkocht",
    chip: "uitverkocht",
    products: (count: number) => (count === 1 ? "1 product" : `${count} producten`),
    soldOutCount: (count: number) => `${count} uitverkocht`,
    tile: (name: string) => `${name} is uitverkocht`,
  },
  promotions: {
    title: "Acties",
    note: "Per actie kiest de eigenaar zelf op welke kanalen ze geldt. Enkel de eigenaar ziet deze sectie.",
    columns: { action: "Actie", on: "Actief" },
    channels: { takeaway: "Afhaal", delivery: "Levering", table: "Tafel" },
    names: {
      day: "Dagpromo pizza",
      day2: "Tweede dagpromo",
      auto: "Automatische korting",
      free: "Gratis product",
      points: "Spaarkaart",
    },
    on: "Aan",
    off: "Uit",
    at: (name: string, channel: string) => `${name} bij ${channel}`,
    percent: "Percentage",
    minimum: "Minimumbedrag",
    product: "Gratis product",
    percentFrom: "% korting vanaf €",
    freeFrom: "gratis vanaf €",
    drinks: "Dranken",
    others: "Andere producten",
    pointsDetail: (from: string, goal: number, reward: string) =>
      `1 punt per product vanaf € ${from} · ${String(goal)}e punt = € ${reward} korting`,
    /** "1 punt per product vanaf € [8] · [11]e punt = € [5] korting", each number per shop. */
    pointsEdit: {
      from: "1 punt per product vanaf €",
      fromLabel: "Punt vanaf bedrag",
      middle: "·",
      goalLabel: "Hoeveelste punt geeft de korting",
      reward: "e punt = €",
      rewardLabel: "Bedrag van de korting",
      off: "korting",
    },
    day: {
      edit: "Wijzigen",
      notSet: "Nog niet ingesteld: kies een categorie, dagen en prijzen",
      title: "Dagpromo instellen",
      days: "Dagen",
      category: "Categorie",
      label: "Woord op de tegel",
      labelHint: "bv. Dagpromo",
      prices: "Dagprijs per formaat",
      pricesHint: "leeg = geen dagprijs voor dat formaat",
      priceOf: (size: string) => `Dagprijs ${size}`,
      usual: (amount: string) => `kaart vanaf ${amount}`,
      except: "Doet niet mee",
      exceptHint: "tik de producten die hun gewone prijs houden",
      save: "Opslaan",
      badPrice: (size: string) => `Geen geldige dagprijs voor ${size}.`,
      noPrice: "Vul minstens één dagprijs in.",
      noDay: "Kies minstens één dag.",
      noLabel: "Vul het woord op de tegel in.",
    },
    /** "Mail de dagpromo naar je klanten" (Furkan, 14/10/2026), under the Dagpromo. */
    dayMail: {
      button: "Mail de dagpromo naar je klanten",
      preview: "Voorbeeld bekijken",
      previewTitle: "Voorbeeld van de mail",
      confirm: (count: number) => `Mail versturen naar ${String(count)} ${count === 1 ? "klant" : "klanten"}?`,
      send: "Versturen",
      sent: (count: number) => `Mail verstuurd naar ${String(count)} ${count === 1 ? "klant" : "klanten"}`,
      reasons: {
        off: "Zet de Dagpromo eerst aan",
        not_today: "Kan enkel op een promodag",
        closed: "De zaak is vandaag al dicht",
        no_sizes: "Geen dagprijs voor een product van de kaart",
        no_recipients: "Geen klanten met een e-mailadres die acties per mail willen",
        sent: (count: number) => `Vandaag al verstuurd naar ${String(count)} ${count === 1 ? "klant" : "klanten"}`,
      },
      ready: (count: number) =>
        `Vandaag naar ${String(count)} ${count === 1 ? "klant" : "klanten"} die acties per mail willen, één keer per dag.`,
    },
    mailing: {
      title: "Rondzending",
      note: (count: number) =>
        `Een eigen mail naar je ${String(count)} ${count === 1 ? "klant" : "klanten"} die acties per mail willen. ` +
        "Elke klant kan zich met één knop uitschrijven; mails over bestellingen en de klantenkaart blijven altijd komen.",
      subject: "Onderwerp",
      subjectHint: "zoals de klant het in zijn mailbox ziet",
      label: "Label",
      labelHint: "witte pil boven de titel, mag leeg",
      heading: "Titel",
      text: "Tekst",
      highlight: "In de witte kader",
      highlightHint: "mag leeg",
      infoTitle: "Kader eronder: titel",
      infoText: "Kader eronder: tekst",
      orderUrl: "Link voor “Bestel online”",
      orderUrlHint: "begint met https://, leeg = geen knop",
      orderUrlWrong: "Een link begint met https://",
      pizza: "Pizza naast de titel",
      callNote: "De knop “Bel en bestel” en het adres komen uit Beheer › Algemeen.",
      logo: "Logo bovenaan",
      logoNone: "geen, de naam van de zaak staat er",
      logoChoose: "Logo kiezen",
      logoReplace: "Ander logo",
      logoRemove: "Weg",
      logoSaved: "Logo opgeslagen",
      preview: "Voorbeeld bekijken",
      previewTitle: "Voorbeeld van de mail",
      test: "Proefmail",
      testTitle: "Proefmail versturen",
      /** Only to the shop itself, at the address Tafo has (Furkan, 30/10/2026: "4a prima"). */
      testTo: "De proefmail gaat naar het e-mailadres van de zaak bij Tafo:",
      testToNote: "Klopt dit adres niet? Vraag Tafo om het aan te passen: info@tafo.be.",
      testNoAddress:
        "Tafo heeft nog geen geldig e-mailadres van de zaak. Vraag Tafo om het in te vullen: info@tafo.be.",
      testSend: "Proefmail versturen",
      testSent: (to: string) => `Proefmail verstuurd naar ${to}`,
      /** The test shop mails only addresses of tafo.be (Furkan, 30/10/2026: "c prima"); the server kept it unsent. */
      testSkipped: (to: string) =>
        `Proefmail niet verstuurd naar ${to}: de testzaak mailt enkel naar adressen van tafo.be.`,
      send: "Versturen…",
      sendTitle: "Rondzending versturen",
      now: "Nu",
      later: "Op een later tijdstip",
      at: "Dag en uur",
      atWrong: "Kies een tijd vanaf nu en binnen twee weken",
      /** Furkan, 30/10/2026: "b oké"; a second one that day is refused by the server, with the day. */
      oncePerDay: "Eén rondzending per dag.",
      confirm: (count: number) => `Mail versturen naar ${String(count)} ${count === 1 ? "klant" : "klanten"}`,
      confirmLater: (count: number, when: string) =>
        `Mail op ${when} versturen naar ${String(count)} ${count === 1 ? "klant" : "klanten"}?`,
      plan: "Inplannen",
      sent: (count: number) => `Rondzending verstuurd naar ${String(count)} ${count === 1 ? "klant" : "klanten"}`,
      planned: (count: number, when: string) =>
        `Rondzending naar ${String(count)} ${count === 1 ? "klant" : "klanten"} gepland op ${when}`,
      missing: "Vul minstens het onderwerp en de titel in",
      none: "Geen klanten met een e-mailadres die acties per mail willen",
      history: "Laatste rondzendingen",
      stateSent: (sent: number, recipients: number) => `${String(sent)} van ${String(recipients)} verstuurd`,
      statePlanned: "gepland, nog niet verstuurd",
      stateCancelled: (sent: number) => (sent === 0 ? "geannuleerd" : `geannuleerd na ${String(sent)} mails`),
      cancel: "Annuleren",
      cancelConfirm: "Deze rondzending annuleren? Mails die nog niet vertrokken, gaan niet meer.",
      cancelled: (count: number) => `Geannuleerd: ${String(count)} ${count === 1 ? "mail" : "mails"} niet verstuurd`,
    },
    saved: "Acties opgeslagen",
    /** On the ticket. */
    autoLine: (percent: number, minimum: string) => `Actie −${String(percent)}% vanaf ${minimum}`,
    /** The red bar above the line buttons once the order reaches the minimum of "Gratis product". */
    chooseFree: (name: string) => `Actie: kies ${name} (gratis) ›`,
    chooseFirst: (name: string) => `Kies eerst de actie: ${name}`,
    freeAdded: "Gratis product toegevoegd (actie)",
    points: (count: number) => `+${String(count)} spaarpunt${count === 1 ? "" : "en"}`,
    receiptLine: "Actie korting",
  },
  staffAdmin: {
    title: "Personeel & rechten",
    intro: (codeRights: string) =>
      `De kassa staat altijd open. Een persoonlijke code van 4 cijfers is enkel nodig voor ${codeRights}, en voor Beheer. In- en uitklokken gebeurt één keer per dag aan de kassa-pc, met de eigen code (uit te schakelen door de zaakvoerder bij Tijdsregistratie). Zo staat bij elke gevoelige actie vast wie ze deed.`,
    add: "+ Medewerker",
    showInactive: "ook uit dienst tonen",
    columns: { name: "Naam", role: "Rol", code: "Code", rights: "Rechten", status: "Status" },
    all: "alles",
    exceptionTitle: "Uitzondering voor deze persoon",
    defaultTitle: "Standaard voor de rol",
    removedTitle: "Uitzondering: afgenomen",
    active: "● actief",
    clockedIn: "● ingeklokt",
    inactive: "● uit dienst",
    edit: "Bewerken",
    ownerOnly: "enkel eigenaar",
    matrix: "Standaardrechten per rol",
    codePill: "code",
    /** The switch in the list (Furkan, 09/10/2026: "zoals een aan/uit schuifschakelaar"). */
    inServiceOf: (name: string) => `${name} in dienst`,
    nowActive: (name: string) => `${name} is weer in dienst`,
    nowInactive: (name: string) => `${name} staat uit dienst en kan niet meer aanmelden`,
    pair: "Toestel koppelen",
    pairOf: (name: string) => `Toestel koppelen voor ${name}`,
    pairName: (name: string) => `Gsm ${name}`,
    dialog: {
      new: "Nieuwe medewerker",
      edit: "Medewerker bewerken",
      name: "Naam",
      role: "Rol",
      code: "Persoonlijke code",
      newCode: "Nieuwe code",
      codeKept: "Leeg laten = de code blijft dezelfde.",
      phone: "Gsm (voor ober- of koerier-app)",
      inService: "In dienst",
      rights: "Rechten",
      ownerAll: "De eigenaar heeft altijd alle rechten.",
      exception: "uitzondering",
      fixed: "vast",
      fixedTitle: "Dit recht hoort vast bij de rol en kan niet als uitzondering gegeven worden.",
      reset: (role: string) => `Terug naar standaard voor ${role}`,
      methods: "Betaalwijzen die deze persoon mag aannemen",
      methodsNote: "Een koerier start met enkel contant.",
      cancel: "Annuleren",
      save: "Opslaan",
      noName: "Vul een naam in.",
      badCode: "De code moet uit 4 cijfers bestaan.",
      noMethod: "Kies minstens één betaalwijze.",
      remove: "Verwijderen",
      removeConfirm: (name: string) =>
        `${name} verwijderen? Dat kan enkel als ${name} nog niets deed in Tafo (geen bestellingen, betalingen of uren). Anders zet je ${name} uit dienst.`,
    },
    removed: (name: string) => `${name} verwijderd`,
    saved: (name: string) => `${name} opgeslagen`,
  },
  printing: {
    title: "Printers & toestellen",
    zones: {
      printers: "1 · Printers",
      output: "2 · Wat drukt elke printer af?",
      kitchen: "3 · Keukenbonnen per product",
      devices: "4 · Toestellen",
      terminal: "5 · Betaalterminal",
      callerId: "6 · Nummerherkenning",
      optional: "(optioneel)",
    },
    columns: {
      name: "Naam",
      printer: "Printer",
      paper: "Papier",
      drawer: "Kassalade",
      receipt: "Bon",
      count: "Aantal",
      product: "Product",
      kind: "Soort",
      till: "Kassa-pc",
      terminal: "Terminal",
    },
    nameLabel: "Naam printer",
    via: (name: string) => ` · via ${name}`,
    yes: "Ja",
    no: "Nee",
    test: "Test",
    remove: "Weg",
    removeConfirm: (name: string) => `Printer "${name}" verwijderen?`,
    removeButton: "Verwijderen",
    removed: (name: string) => `${name} verwijderd`,
    testSent: (name: string, paper: number) => `Testafdruk naar ${name} (${String(paper)} mm)`,
    add: "+ Printer toevoegen",
    noPrinters: "Nog geen printers. Voeg de printers van de zaak toe.",
    // one table instead of Werkwijze keuken, the copies per printer and Afrekeningen (Furkan, 11/10/2026:
    // "het werkt, biraz ingewikkeld, basitlestirebiliriz")
    orderKinds: { takeaway: "Afhaal", delivery: "Levering", table: "Tafel" },
    output: {
      what: {
        none: "—",
        ticket: "Keukenbon",
        all: "Keukenbon (alles)",
        receipt: "Afrekening",
        ticketReceipt: "Keukenbon + afrekening",
        allReceipt: "Keukenbon (alles) + afrekening",
      },
      whatOf: (printer: string, kind: string) => `${printer} bij ${kind}`,
      countOf: (printer: string, kind: string) => `Aantal afrekeningen ${printer} bij ${kind}`,
      onlyIfMakes: "enkel als er iets voor deze printer bij is",
      saved: (printer: string, kind: string, what: string) => `${printer} · ${kind}: ${what}`,
      times: (what: string, count: number, onlyIfMakes: boolean) =>
        `${what} ${String(count)}×${onlyIfMakes ? " als er iets voor deze printer bij is" : ""}`,
      copyLeft: (count: number) => `en nog ${String(count)}× bij het bestellen`,
      ifMakesLeft: (count: number) => `en nog ${String(count)}× als er iets voor deze printer bij is`,
      printsNothing: (kinds: string) => `Maakt producten, maar drukt niets af bij ${kinds}.`,
    },
    z: "Z-rapport",
    zNote: "Het X- en Z-rapport en de afrekenbon van een koerier komen uit:",
    zPrinter: "Printer Z-rapport",
    zCount: "Aantal Z-rapport",
    // "Per kassa" (Furkan, 09/10/2026): the receipt out of the printer of the till where it is paid
    perTill: "Per kassa",
    perTillNote:
      "Heeft elke kassa-pc een eigen bonprinter? Kies hem hier: de kassabon van wat op die kassa afgerekend of opnieuw afgedrukt wordt, komt uit zijn printer, zoveel keer als in de tabel. Het Z-rapport blijft op de printer hierboven.",
    perTillDefault: "Zoals hierboven",
    perTillOf: (name: string) => `Bonprinter van ${name}`,
    perTillSaved: (till: string, printer: string | null) =>
      printer === null ? `${till}: kassabon zoals in de tabel` : `${till}: kassabon op ${printer}`,
    // the printable width in dots (Furkan, 09/10/2026: a Bixolon printed too large, the right side cut off)
    dots: {
      label: "Breedte",
      of: (name: string) => `Afdrukbreedte ${name}`,
      auto: "Automatisch",
      option: (dots: number) => `${String(dots)} punten`,
      hint: "Valt de rechterkant weg of staat alles te groot? Kies een kleinere breedte en druk opnieuw een test af.",
      saved: (name: string, dots: number | null) =>
        dots === null ? `${name}: breedte automatisch` : `${name}: ${String(dots)} punten breed`,
    },
    exceptions: (names: string) => `Uitzondering: ${names}`,
    exception: "uitzondering",
    likeCategory: "zoals categorie",
    on: (what: string, printer: string) => `${what} op ${printer}`,
    kitchenCopies: "Aantal keukenbonnen",
    kitchenCopiesOf: (name: string) => `Aantal keukenbonnen ${name}`,
    nothingRouted:
      "Nog niets ingesteld: een tafel die naar de keuken gaat, drukt zo nergens een keukenbon af. Zet hieronder de categorieën op hun printer, of zet bij 2 een printer op “Keukenbon (alles)” voor tafel.",
    deviceName: "Naam toestel",
    thisDevice: "dit toestel",
    kindLabel: "Soort",
    deviceTypes: {
      pc: "Kassa-pc (Windows)",
      sunmi: "Sunmi V3 (met printer)",
      iphone: "iPhone",
      ipad: "iPad",
      android: "Android",
      tablet: "Tablet",
    },
    deviceRoles: {
      till: "kassa",
      waiter: "Tafo-app · ober",
      courier: "Tafo-app · koerier",
      app: "Tafo-app · kiest nog: ober of koerier",
    },
    pairedOn: (date: string, role: string) => `gekoppeld ${date} · ${role}`,
    addDevice: "+ Nieuw toestel",
    newDevice: {
      title: "Nieuw toestel",
      which: "Welk toestel is dit?",
      name: "Naam van dit toestel",
      info: {
        pc: "Groot kassascherm met toetsenbord en sneltoetsen, met de Tafo-app voor Windows. Bonnen volgens Printers.",
        sunmi:
          "De Tafo-app op de Sunmi: ober of koerier, één keer te kiezen op het toestel. Bonnen gaan voorlopig via de hoofdcomputer naar de printers.",
        iphone: "De Tafo-app voor ober of koerier. Bonnen gaan via de hoofdcomputer naar de printers.",
        ipad: "De Tafo-app met tafels en bestelling naast elkaar. Bonnen gaan via de hoofdcomputer naar de printers.",
        android: "De Tafo-app voor ober of koerier. Bonnen gaan via de hoofdcomputer naar de printers.",
        tablet: "De Tafo-app met tafels en bestelling naast elkaar. Bonnen gaan via de hoofdcomputer naar de printers.",
      } satisfies Record<DeviceType, string>,
      make: "Koppelcode maken",
      codeLabel: "Koppelcode",
      valid: (until: string) => `Eén keer te gebruiken, geldig tot ${until} (15 minuten).`,
      whereTill: (url: string) =>
        `Open op die computer de Tafo-app voor Windows (of de kassa op ${url}) en typ deze code bij "Kassa koppelen".`,
      whereApp: (url: string) =>
        `Open op dat toestel ${url} en typ deze code. Kies daarna ober of koerier en zet de app op het beginscherm.`,
      done: "OK",
    },
    unpair: "Ontkoppelen",
    unpairConfirm: (name: string) =>
      `"${name}" ontkoppelen? Dit toestel moet daarna opnieuw door de eigenaar gekoppeld worden.`,
    unpaired: (name: string) => `${name} ontkoppeld`,
    looseTerminal: "Losse terminal (niet gekoppeld)",
    linkTerminal: "Terminal koppelen",
    linkLater: "Koppelen met CCV, Worldline, Viva Wallet of SumUp volgt na de pilot.",
    noTill: "Nog geen kassa-pc gekoppeld.",
    saved: "Opgeslagen",
    addDialog: {
      title: "Printer toevoegen",
      note: "Kies een printer die al gevonden werd. Model, verbinding en papierbreedte worden automatisch ingevuld; je geeft hem enkel een naam.",
      installedOn: (name: string) => `Geïnstalleerd op ${name} (Windows)`,
      thisComputer: "deze computer",
      usbNote: (name: string) =>
        `USB-printers werken enkel via deze computer. Andere toestellen sturen hun bonnen door via ${name}.`,
      network: "Gevonden in het netwerk",
      nothingYet:
        "Nog niets gevonden. Zodra de Tafo-app voor Windows op de kassa-pc draait, verschijnen de printers hier vanzelf.",
      added: "al toegevoegd",
      pick: (paper: number) => `${String(paper)} mm · kiezen`,
      nameStep: "Naam voor deze printer",
      nameAndIp: "Naam en IP-adres",
      namePlaceholder: (kitchen: boolean) => (kitchen ? "bv. Keukenprinter" : "bv. Printer toog"),
      manualPlaceholder: "bv. Drankenprinter toog",
      ipPlaceholder: "IP-adres, bv. 192.168.1.70",
      ipLabel: "IP-adres",
      badIp: "Vul een geldig IP-adres in, bv. 192.168.1.70.",
      add: "Toevoegen",
      manual: "Staat je printer er niet tussen? Manueel invoeren (IP-adres)",
      networkPrinter: "Netwerkprinter",
      done: (name: string) => `${name} toegevoegd`,
    },
  },
  /** Afdrukken (blok I, ADR 0010): what the till says after it sent something to the printers (prototype). */
  /** The bridges of Takeaway and Uber Eats on the till PC (Furkan, 23/10/2026: Sara's Live Orders hung unseen). */
  bridges: {
    name: (bridge: "takeaway" | "ubereats") => (bridge === "takeaway" ? "Takeaway-brug" : "Uber Eats-brug"),
    page: (bridge: "takeaway" | "ubereats") => (bridge === "takeaway" ? "Live Orders" : "de Uber Eats-pagina"),
    silent: (name: string, page: string, since: string) =>
      `⚠ ${name}: geen contact sinds ${since}. Staat Chrome met ${page} open op de kassa-pc?`,
    login: (name: string, page: string, since: string) =>
      `⚠ ${name}: opnieuw inloggen op ${page} (sinds ${since}). Tot dan komen er geen bestellingen binnen.`,
    hanging: (name: string, page: string, since: string) =>
      `⚠ ${name}: ${page} hangt sinds ${since}. De brug laadt de pagina zelf opnieuw; blijft dit staan, kijk op de kassa-pc.`,
    waiting: (name: string, count: number) =>
      `⚠ ${name}: ${count === 1 ? "1 bestelling" : `${String(count)} bestellingen`} nog niet in Tafo, wordt elke 30 seconden opnieuw geprobeerd.`,
  },
  print: {
    /** "Kassaprinter toog (80 mm) ×2" */
    printer: (name: string, paper: number, copies = 1) =>
      `${name} (${String(paper)} mm)${copies > 1 ? ` ×${String(copies)}` : ""}`,
    kitchen: (printers: string) => `Keukenbon → ${printers}`,
    copy: (printers: string) => `Kopie afrekening → ${printers}`,
    receipt: (printers: string) => `Kassabon → ${printers}`,
    drawer: "Kassalade open (contant)",
    extra: (items: string) => `Keukenbon BIJBESTELLING → ${items}`,
    removed: (items: string) => `Keukenbon GESCHRAPT → ${items}`,
    copyChanged: (changes: string) => `Kopie aangepaste afrekening → keuken (GEWIJZIGD: ${changes})`,
    plus: (items: string) => `+ ${items}`,
    minus: (items: string) => `− ${items}`,
    newReceipt: (code: string, total: string) => `Nieuwe kassabon ${code} · totaal ${total}`,
    cancelled: (printers: string) => `Keukenbon GEANNULEERD → ${printers}`,
    /** A table to the kitchen with no kitchen printer set up (Papermoon, 08/10/2026). */
    unrouted: (items: string) =>
      `Geen keukenbon afgedrukt: geen keukenprinter ingesteld voor ${items}. Zie Beheer › Printers & toestellen.`,
    reprinted: (code: string, printer: string) => `Bon ${code} afgedrukt op ${printer}`,
    reprintedMany: (count: number, printers: string) => `${String(count)} bonnen afgedrukt op ${printers}`,
    /** "X-rapport → Kassaprinter toog", "Afrekenbon Mehmet → Kassaprinter toog" */
    to: (what: string, printer: string) => `${what} → ${printer}`,
    zClosed: (printer: string) => ` · Z-bon → ${printer}`,
    settled: (name: string, printer: string) => `${name} afgerekend · afrekenbon → ${printer}`,
    /** The delivery receipt right after Bestellen (prototype openReceipt). */
    printedOn: [
      "Afgedrukt op",
      (paper: number) => ` · ${String(paper)} mm papier. De breedte volgt automatisch uit de gekozen printer.`,
    ] as const,
    /** The top bar when something is wrong with printing (all tills). */
    offline: (waiting: number) =>
      `🖨 Printers offline: de Tafo-app voor Windows draait op geen enkele kassa-pc.${
        waiting > 0 ? ` ${waiting === 1 ? "1 afdruk wacht" : `${String(waiting)} afdrukken wachten`}.` : ""
      }`,
    failed: (count: number) => (count === 1 ? "🖨 1 afdruk niet gelukt" : `🖨 ${String(count)} afdrukken niet gelukt`),
    show: "Bekijken",
    failedToast: (title: string, printer: string) => `Niet afgedrukt: ${title} op ${printer}`,
    problems: {
      title: "Afdrukken",
      note: "Wat de voorbije uur niet afgedrukt werd. Kijk de printer na (aan, papier, kabel of netwerk) en druk opnieuw af.",
      columns: { time: "Tijd", what: "Afdruk", printer: "Printer", reason: "Reden" },
      expired: "niet op tijd afgedrukt: de Tafo-app voor Windows draaide niet",
      again: "Opnieuw",
      againAll: "Alles opnieuw",
      dismiss: "Negeren",
      queued: (title: string, printer: string) => `${title} opnieuw naar ${printer}`,
      none: "Alles is afgedrukt.",
      agents: "Tafo-app voor Windows",
      noAgent: "Draait op geen enkele kassa-pc: niets wordt afgedrukt tot ze terug draait.",
      agent: (name: string, version: string | null) => `${name}${version ? ` · versie ${version}` : ""}`,
      waiting: (count: number) => (count === 1 ? "1 afdruk wacht" : `${String(count)} afdrukken wachten`),
    },
    /** Beheer › Printers & toestellen, 4 · Toestellen: the app on a till computer. */
    agentOn: (version: string | null) => `🖨 Tafo-app actief${version ? ` · versie ${version}` : ""}`,
    agentOff: "🖨 Tafo-app niet actief op deze computer",
    /** The print page of the Tafo app for Windows: kitchen tickets and the test. */
    ticket: {
      titles: {
        new: "KEUKENBON",
        extra: "BIJBESTELLING",
        removed: "GESCHRAPT",
        cancelled: "GEANNULEERD",
        moved: "VERPLAATST",
      },
      notAnyMore: "Niet meer maken:",
      ordered: (time: string) => `Besteld ${time}`,
      ready: (time: string) => `Klaar om ${time}`,
      deliverAt: (time: string) => `Bezorgen om ${time}`,
      table: (table: string) => `TAFEL ${table}`,
      moved: (from: string, to: string) => `Van tafel ${from} naar tafel ${to}`,
      bring: "Breng alles naar de nieuwe tafel.",
      name: (name: string) => `Naam: ${name}`,
      note: (note: string) => `Opmerking: ${note}`,
      reason: (reason: string) => `Reden: ${reason}`,
      by: (who: string, time: string) => `${who} · ${time}`,
    },
    copyChanges: (changes: string) => `GEWIJZIGD: ${changes}`,
    test: {
      title: "TESTAFDRUK",
      printer: (name: string, paper: number) => `${name} · ${String(paper)} mm`,
      ok: "Staat dit mooi en volledig op het papier? Dan is de printer in orde.",
      narrower:
        "Valt de rechterkant weg of staat alles te groot? Kies in Beheer › Printers & toestellen een kleinere breedte.",
      ruler: "|....:....|....:....|....:....|....:....|",
      qr: "Deze QR-code moet leesbaar zijn.",
    },
    page: {
      loading: "Afdruk wordt voorbereid…",
      missing: "Deze afdruk bestaat niet (meer).",
    },
  },
  floorAdmin: {
    title: "Tafelplan",
    qrTitle: "QR-bestellen aan tafel",
    qrOff: "uit",
    qrOnNow: "aan",
    qrOn: "Aanzetten",
    qrOffButton: "Uitzetten",
    qrLater:
      "Zet je dit aan, dan bestellen gasten zelf met de QR-code op hun tafel. Je kunt het altijd weer uitzetten.",
    qrSwitched: (on: boolean) => (on ? "QR-bestellen aan tafel staat aan" : "QR-bestellen aan tafel staat uit"),
    qrApprove: "Eerst goedkeuren aan de kassa: elke QR-bestelling komt pas in de keuken als de kassa ze goedkeurt",
    qrApproveSwitched: (on: boolean) =>
      on ? "QR-bestellingen worden eerst aan de kassa goedgekeurd" : "QR-bestellingen gaan meteen naar de keuken",
    qrHint: "Gasten scannen de code op hun tafel en bestellen zelf; de bestelling komt op de tafel en in de keuken.",
    addRoom: "＋ Ruimte",
    saved: "✓ bewaard",
    add: { rect: "＋ Tafel", round: "＋ Ronde tafel", bar: "＋ Toog", wall: "＋ Muur", text: "＋ Tekst" },
    snap: "Uitlijnen op raster",
    lock: {
      on: "🔒 Vergrendeld",
      off: "🔓 Ontgrendeld",
      hint: "Vergrendeld verschuift er niets per ongeluk; een tafel aantikken om ze aan te passen kan wel. Tik om te ontgrendelen of te vergrendelen.",
      refused: "Het plan is vergrendeld: tik eerst op 🔒 Vergrendeld om tafels te verschuiven.",
    },
    hint: "Pijltjestoetsen verschuiven de geselecteerde tafel · Delete verwijdert ze.",
    pick: "Klik op een tafel om ze aan te passen.",
    table: (label: string) => `Tafel ${label}`,
    label: "Nummer of naam",
    shape: "Vorm",
    rect: "▭ Rechthoek",
    round: "◯ Rond",
    seats: "Aantal plaatsen",
    fewer: "Minder plaatsen",
    more: "Meer plaatsen",
    width: "Breedte",
    height: "Hoogte",
    duplicate: "Dupliceren",
    remove: "Verwijderen",
    decor: { bar: "Toog", wall: "Muur", text: "Tekst" },
    text: "Tekst",
    room: "Ruimte",
    roomName: "Naam",
    roomCount: (tables: number, seats: number) => `${String(tables)} tafels · ${String(seats)} plaatsen`,
    removeRoom: "Ruimte verwijderen",
    busyRoom: "Er zijn bezette tafels in deze ruimte.",
    removeRoomConfirm: (name: string, tables: number) => `Ruimte "${name}" met ${String(tables)} tafels verwijderen?`,
    reset: "Standaardindeling herstellen",
    resetConfirm: "De standaardindeling herstellen? Je eigen indeling gaat verloren.",
    resetButton: "Herstellen",
    payFirst: "Eerst alle tafels afrekenen",
    noLabel: "Geef een nummer of naam.",
    exists: (label: string) => `Tafel ${label} bestaat al.`,
    busyRename: "Tafel is bezet; hernoem ze na het afrekenen.",
    busyRemove: (label: string) => `Tafel ${label} is nu bezet en kan niet verwijderd worden.`,
    blocked: "Daar staat al iets",
    tooBig: "Zo groot raakt het een andere tafel",
    seatsShort: (seats: number) => `${String(seats)}p`,
    roomExists: "Er is al een ruimte met deze naam.",
  },
  /** Hours per day (Leveringszones, Algemeen). */
  hours: {
    days: ["Maandag", "Dinsdag", "Woensdag", "Donderdag", "Vrijdag", "Zaterdag", "Zondag"],
    from: (day: string) => `${day} van`,
    to: (day: string) => `${day} tot`,
    closed: "gesloten",
    placeholder: "uu:mm",
    badTime: "Tijd als uu:mm, bv. 17:00",
    // grouped as on the receipt: "Maandag t/m Zondag: 17:00 tot 23:00"
    through: (first: string, last: string) => `${first} t/m ${last}`,
    and: (first: string, second: string) => `${first} & ${second}`,
    range: (from: string, to: string) => `${from} tot ${to}`,
  },
  zonesAdmin: {
    title: "Leveringszones",
    standard: "Standaard",
    fee: "Leveringskost €",
    /** A phone: the same on one line with the rest (Furkan, 18/10/2026). */
    feeShort: "Kost €",
    freeFrom: "gratis vanaf €",
    never: "nooit",
    columns: {
      on: "Aan",
      postcode: "Postcode",
      zone: "Gemeente",
      minimum: "Minimum",
      fee: "Kost",
      freeFrom: "Gratis vanaf",
      far: "Afstand",
    },
    onOff: (zone: string) => `${zone} aan of uit`,
    turnedOn: (zone: string) => `${zone} aan`,
    turnedOff: (zone: string) => `${zone} uit`,
    edit: "Bewerken",
    editOf: (zone: string) => `Bewerken ${zone}`,
    add: "+ Gemeente",
    none: "Nog geen gemeenten. Voeg de gemeenten toe waar de zaak levert.",
    farText: (from: string | null, extra: string | null, max: string | null) =>
      [from !== null && extra !== null ? `vanaf ${from} km +${extra}` : null, max !== null ? `max ${max} km` : null]
        .filter(Boolean)
        .join(" · "),
    hours: "Bezorgtijden",
    hoursNote: "Buiten deze uren toont de website geen levering meer en waarschuwt de kassa bij een levering.",
    dialog: {
      new: "Nieuwe zone",
      edit: "Zone bewerken",
      postcode: "Postcode",
      zone: "Gemeente / deelgemeente",
      minimum: "Minimumbedrag €",
      fee: "Leveringskost €",
      freeFrom: "Gratis vanaf €",
      standard: (value: string) => `leeg = standaard ${value}`,
      on: "Aan (we leveren hier)",
      far: "Uitzondering op afstand",
      optional: "optioneel",
      fromKm: "Vanaf",
      kmFromShop: "km: toeslag €",
      maxKm: "Niet leveren verder dan",
      km: "km",
      fromKmLabel: "Toeslag vanaf km",
      extraLabel: "Toeslag €",
      maxLabel: "Maximum km",
      farNote:
        "De toeslag komt bovenop de leveringskost en blijft ook boven het gratis-bedrag. De afstand per adres wordt berekend zodra de adressen van de klanten gekend zijn (Klanten); tot dan geldt enkel de leveringskost.",
      remove: "Verwijderen",
      removeConfirm: (zone: string) =>
        `${zone} uit de leveringszones verwijderen? Klanten met dit adres kan je dan niet meer laten leveren (tenzij je de zone opnieuw toevoegt).`,
      cancel: "Annuleren",
      save: "Opslaan",
      badPostcode: "Postcode = 4 cijfers.",
      noZone: "Vul de gemeente in.",
      exists: "Deze gemeente staat al in de lijst met die postcode.",
      noMinimum: "Vul een minimumbedrag in (0 mag).",
      badAmount: "Vul een geldig bedrag in, bv. 0,50.",
      farIncomplete: "Vul bij de toeslag zowel de afstand als het bedrag in.",
      farMax: "Het maximum moet verder liggen dan de toeslag-afstand.",
      saved: (zone: string) => `${zone} opgeslagen`,
      removed: (zone: string) => `${zone} verwijderd`,
    },
  },
  loyalty: { line: "Spaarkaart" },
  customersAdmin: {
    title: "Klanten",
    count: (total: string) => `${total} klanten`,
    search: "Zoek op naam, telefoon, straat of e-mail…",
    sort: { count: "Meeste bestellingen", name: "Naam", zone: "Adres" },
    sortLabel: "Sorteren",
    // only what a search finds, never a whole list (Furkan, 11/10/2026)
    searchFirst:
      "Zoek een klant (minstens 3 tekens) om hem te bekijken of aan te passen. Een volledige lijst van de klanten wordt niet getoond.",
    more: (shown: number, found: string) => `De eerste ${String(shown)} van ${found} gevonden: zoek preciezer.`,
    columns: { name: "Naam", phone: "Telefoon", address: "Adres", orders: "Bestellingen", points: "Punten" },
    note: "notitie",
    newsletterPending: "nieuwsbrief: wacht op bevestiging",
    edit: "Bewerken",
    editOf: (name: string) => `Bewerken ${name}`,
    none: "Geen klanten gevonden.",
    footer: "Klanten worden nooit automatisch gewist. Interne notities ziet enkel het personeel, nooit de klant.",
    dialog: {
      customer: "Klant",
      phone: "Telefoon",
      name: "Naam",
      email: "E-mail",
      street: "Straat",
      number: "Nr",
      box: "Bus",
      zone: "Gemeente",
      points: "Spaarpunten",
      newsletter: "Wil de nieuwsbrief",
      // the customer says yes himself, with the link in a mail (Furkan, 30/10/2026)
      newsletterPending: "wacht op bevestiging",
      newsletterAsk: "Na Opslaan krijgt de klant een mail om dit te bevestigen.",
      note: "Interne notitie",
      noteHint: "enkel voor het personeel",
      orders: "Bestellingen",
      total: "Totaal besteld in Tafo",
      last: "Laatste bestelling",
      badEmail: "Geen geldig e-mailadres.",
      cancel: "Annuleren",
      save: "Opslaan",
      saved: "Klant opgeslagen",
      confirmationSent: "Bevestigingsmail verstuurd",
      confirmationTooSoon:
        "Klant opgeslagen, de nieuwsbrief nog niet: er ging minder dan 24 uur geleden al een bevestigingsmail naar deze klant of dit adres. Probeer het later opnieuw.",
      confirmationTooMany:
        "Klant opgeslagen, de nieuwsbrief nog niet: de zaak verstuurde de laatste 24 uur al het maximum aan bevestigingsmails. Probeer het later opnieuw.",
    },
  },
  reports: {
    title: "Rapporten & Z",
    tabs: { day: "Vandaag", archive: "Z-archief", accountant: "Boekhouder", platforms: "Platformen" },
    /** The orange bar on every till while a day has no Z (Furkan, 11/10/2026). */
    banner: {
      one: (day: string) => `De Z van ${day} is nog niet gemaakt. Maak hem in Beheer › Rapporten & Z.`,
      many: (count: number, first: string, last: string) =>
        `Al ${String(count)} dagen zonder Z (${first} tot ${last}). Maak ze in Beheer › Rapporten & Z.`,
    },
    /** The Z page (Furkan, 11/10/2026): the online platforms first, then the whole day, then the Z. No drawer. */
    page: {
      missing: (count: number) =>
        count === 1 ? "De Z van deze dag is nog niet gemaakt:" : `Al ${String(count)} dagen zonder Z:`,
      dayButton: (day: string, orders: number) =>
        `${day} · ${String(orders)} ${orders === 1 ? "bestelling" : "bestellingen"}`,
      today: "Vandaag",
      late: "laat",
      liveNote: "Tussenstand (X) van deze dag, van 06:00 tot 06:00. Verandert live mee.",
      lateNote: "Deze dag heeft nog geen Z. Vul de platformen in en sluit de dag af.",
      ordersFromTo: (first: string, last: string) => `Bestellingen van ${first} tot ${last}.`,
      platforms: "Bestelplatformen",
      total: "Totaal van de dag",
      till: "Omzet kassa",
      platformsRevenue: "+ omzet platformen",
      dayTotal: "Dagtotaal",
      // the linked platforms under the day total (Antalya, 17/10/2026): already in it, never added
      linked: "Waarvan via platformen",
      linkedNote: "Zit al in de omzet kassa hierboven, wordt niet opgeteld.",
      linkedOnline: (amount: string) => `online ${amount}`,
      // one Z per day: a new one overwrites it (Furkan, 11-12/10/2026)
      closed: (number: number, time: string) =>
        `Z ${String(number)} van deze dag werd om ${time} gemaakt. Een nieuwe Z overschrijft die: hij telt de hele dag, krijgt hetzelfde nummer en de vorige wordt verwijderd.`,
      // an order added or changed after the Z (Furkan, 11/10/2026: the Z is no obstacle)
      after: (count: number, number: number) =>
        `Sinds Z ${String(number)} ${count === 1 ? "is 1 bestelling" : `zijn ${String(count)} bestellingen`} bijgekomen of gewijzigd: maak een nieuwe Z, dan telt alles mee.`,
      closeAgain: (number: number) => `Z opnieuw afsluiten en afdrukken (overschrijft Z ${String(number)})`,
      noRight: (name: string) =>
        `${name} heeft geen recht op de dagafsluiting (Z). De eigenaar zet dat aan in Personeel & rechten.`,
      closeLate: (day: string) => `Z van ${day} afsluiten en afdrukken`,
    },
    boxes: { revenue: "Omzet", payments: "Betaalwijzen", vat: "BTW", checks: "Controle" },
    orders: "Bestellingen",
    average: "Gemiddelde bon",
    channels: { takeaway: "Afhaal", delivery: "Levering", table: "Tafel" } satisfies Record<Channel, string>,
    // the platforms of the Z page in Betaalwijzen, each on its own line (Furkan, 13/10/2026)
    // a box with nothing on it: a line of 0 is not shown (Furkan, 13/10/2026: "geen overbodige info")
    none: { orders: "Nog geen bestellingen.", payments: "Nog geen betalingen.", checks: "Niets te melden." },
    platformOnline: "Online betaald (platformen)",
    platformCash: "Online cash (platformen)",
    vatColumns: { rate: "Tarief", base: "Basis", vat: "BTW", total: "Totaal" },
    discounts: "Kortingen",
    free: "Gratis gegeven",
    cancelled: "Annuleringen",
    countAmount: (count: number, amount: string) => `${String(count)} · ${amount}`,
    cancelledBy: (code: string, name: string) => `${code} door ${name}`,
    closing: "Dagafsluiting (Z)",
    finishFirst: "Eerst afwerken:",
    open: {
      deliveries: (count: number) => `${String(count)} levering(en) nog niet geleverd`,
      couriers: (names: string) => `koerier(s) nog niet afgerekend: ${names}`,
      tables: (tables: string) => `open tafel(s): ${tables}`,
      other: (count: number) => `${String(count)} bestelling(en) nog niet betaald`,
      parked: (count: number) => `${String(count)} geparkeerde bestelling(en) in de wachtlijst`,
    },
    printX: "🖨 X-rapport afdrukken",
    closeZ: "Z afsluiten en afdrukken",
    dialog: {
      platformsNote:
        "Voor platformen die niet gekoppeld zijn: neem de dagomzet en wat online betaald is over uit het " +
        "dagoverzicht van dat platform. De rest telt als cash.",
      linkedNote:
        "Via de koppeling — deze bestellingen zitten al in de dagomzet en de betaalwijzen, dus ze tellen niet " +
        "opnieuw mee. Enkel ter info en controle.",
      columns: { platform: "Platform", revenue: "Dagomzet €", online: "Online betaald €" },
      revenueOf: (name: string) => `Dagomzet ${name}`,
      onlineOf: (name: string) => `Online betaald ${name}`,
      tooHigh: (names: string) => `${names}: online betaald kan niet meer zijn dan de dagomzet`,
      none: "Geen platformen ingesteld.",
      noneMore: "Geen extra (niet-gekoppelde) platformen om zelf in te vullen.",
      manage: "Platformen toevoegen of verwijderen",
      badAmount: (names: string) => `${names}: ongeldig bedrag`,
      closed: (number: number, amount: string) => `Z ${String(number)} afgesloten · ${amount}`,
    },
    bon: {
      xTitle: "X-rapport (tussenstand)",
      zTitle: (number: number) => `Z-bon ${String(number)}`,
      xHeading: "X-RAPPORT · TUSSENSTAND",
      zHeading: (number: number) => `Z-RAPPORT ${String(number)}`,
      /** On the Z-bon, above its day: no number (Furkan, 13/10/2026). */
      zName: "Z Rapport",
      /** A Z from which an order was removed after the closing (Furkan, 05/10/2026). */
      corrected: "HERREKEND",
      correction: (date: string, time: string, by: string, code: string, amount: string, reason: string) =>
        `${date} ${time} · ${by}: bestelling ${code} (${amount}) verwijderd. Reden: ${reason}`,
      original: "OORSPRONKELIJKE Z",
      originalNote: (count: number) =>
        count === 1
          ? "Zoals afgedrukt bij de afsluiting. Daarna werd 1 bestelling verwijderd: zie de herrekende Z."
          : `Zoals afgedrukt bij de afsluiting. Daarna werden ${String(count)} bestellingen verwijderd: zie de herrekende Z.`,
      vatNumber: (number: string) => `BTW ${number}`,
      sinceZ: (number: number, date: string, time: string) => `sinds Z ${String(number)} van ${date} ${time}`,
      printed: (date: string, time: string) => `afgedrukt ${date} ${time}`,
      /** The day a report is about, big under its title (Furkan, 11/10/2026). */
      ordersFromTo: (first: string, last: string) => `bestellingen ${first} – ${last}`,
      noOrders: "geen bestellingen",
      late: "LAAT AFGESLOTEN",
      orders: "Bestellingen",
      total: "TOTAAL",
      platformsTotal: "Totaal platformen",
      dayTotal: "DAGTOTAAL",
      // the linked platforms (Mymenu.be, Takeaway.com, Uber Eats) under the total: already in it (Antalya, 17/10/2026)
      linkedTitle: "WAARVAN VIA PLATFORMEN",
      linkedNote: "Al inbegrepen in het totaal hierboven, niet opgeteld",
      linkedOnline: "waarvan online betaald",
      linkedTotal: "Totaal via platformen",
      // per rate the revenue incl. VAT, as in the kasboek (Furkan, 12/10/2026)
      vatLine: (rate: number) => `${String(rate)}% omzet incl. btw`,
      discounts: "Kortingen",
      free: "Gratis",
      cancelled: "Annuleringen",
      tips: "Fooien (buiten omzet)",
      thirdParty: "Geïnd voor Takeaway: servicekosten (buiten omzet)",
      float: "Wisselgeld start",
      tillCash: "Contant verkoop kassa",
      drawerNow: "CONTANT IN DE LADE (NU)",
      stillOpen: "NOG OPEN",
      xFoot1: "Tussenstand · de dag is NIET afgesloten",
      xFoot2: "Platformen komen pas in de Z",
      kept: "Bewaard in Tafo · niet wijzigbaar",
      print: "🖨 Afdrukken",
    },
    archive: {
      columns: {
        number: "Z",
        day: "Dag",
        closed: "Afgesloten",
        till: "Kassa",
        platforms: "Platformen",
        dayTotal: "Dagtotaal",
      },
      late: "laat",
      open: "Z-bon",
      openOf: (number: number) => `Z-bon ${String(number)}`,
      corrected: "herrekend",
      correctedTitle: (count: number) =>
        count === 1
          ? "Na de afsluiting werd 1 bestelling verwijderd"
          : `Na de afsluiting werden ${String(count)} bestellingen verwijderd`,
      original: "Oorspronkelijk",
      originalOf: (number: number) => `Z-bon ${String(number)} zoals afgedrukt`,
      note: "Elke Z blijft bewaard in het systeem en kan niet gewijzigd of gewist worden. Een Z-bon kan je altijd opnieuw bekijken of afdrukken. Verwijdert de eigenaar na de afsluiting nog een foutieve bestelling, dan wordt de Z herrekend: de Z zoals afgedrukt en elke verwijdering met haar reden blijven bewaard.",
      none: "Nog geen Z-rapporten.",
    },
    accountant: {
      note: "Per kwartaal een Excel met één lijn per dag (Z): omzet, BTW per tarief, betaalwijzen, aantal bestellingen, platformen en contant in de lade. Voor de boekhouder.",
      quarter: "Kwartaal",
      download: "⬇ Excel downloaden",
      show: "Bekijken",
      mail: "Na elk kwartaal automatisch mailen naar de boekhouder",
      email: "e-mail boekhouder",
      ownerOnly: "Het mailen naar de boekhouder zet enkel de eigenaar aan of uit.",
      saved: (email: string) => `Boekhouder: ${email}`,
      badEmail: "Geen geldig e-mailadres.",
      none: "Geen Z-rapporten in dit kwartaal.",
      noData: "Geen gegevens in dit kwartaal",
      downloaded: (name: string) => `Excel opgeslagen: ${name}`,
      total: "Totaal",
      days: (count: number) => `${String(count)} dagen`,
    },
    platforms: {
      note: "De online platformen waarmee deze zaak werkt. Bij de dagafsluiting vul je per platform de dagomzet en het online betaalde bedrag in; contant aan de deur rekent Tafo zelf uit.",
      none: "Nog geen platformen.",
      up: "Omhoog",
      upOf: (name: string) => `${name} omhoog`,
      remove: "Verwijderen",
      removeOf: (name: string) => `${name} verwijderen`,
      placeholder: "bv. Deliveroo, Just Eat, eigen webshop…",
      newLabel: "Nieuw platform",
      add: "+ Platform toevoegen",
      exists: (name: string) => `${name} staat al in de lijst`,
      added: (name: string) => `${name} toegevoegd`,
      removeConfirm: (name: string) => `${name} verwijderen uit de platformen?`,
      removed: (name: string) => `${name} verwijderd`,
      footer:
        "Een verwijderd platform verdwijnt enkel uit de volgende afsluitingen; oude Z-rapporten en kwartaaloverzichten houden hun cijfers.",
    },
  },
  time: {
    button: "⏱ Klok",
    clockedInCount: (count: number) => `${String(count)} ingeklokt`,
    code: "In- of uitklokken",
    status: {
      in: (time: string) => `● ingeklokt sinds ${time}`,
      done: (from: string, to: string) => `● vandaag al in- en uitgeklokt (${from} – ${to})`,
      out: "● nog niet ingeklokt",
    },
    once: "In- en uitklokken gebeurt één keer per dag. Klopt er iets niet, dan past de zaakvoerder het aan in Beheer › Tijdsregistratie.",
    clockIn: "Inklokken",
    clockOut: "Uitklokken",
    cancel: "Annuleren",
    close: "Sluiten",
    clockedIn: (name: string, time: string) => `${name} ingeklokt om ${time}`,
    clockedOut: (name: string, time: string) => `${name} uitgeklokt om ${time}`,
    admin: {
      title: "Tijdsregistratie",
      use: "In- en uitklokken gebruiken in deze zaak",
      ownerOnly: "Enkel de zaakvoerder kan dit aan- of uitzetten. ",
      note: "Staat het aan, dan klokt elke medewerker één keer per dag in en één keer uit, met de eigen code, aan de kassa-pc (knop ⏱ Klok bovenaan). Niet in de ober- of koerier-app.",
      today: "Vandaag",
      columns: { name: "Naam", in: "In", out: "Uit", duration: "Duur", status: "Status" },
      clockedIn: "● ingeklokt",
      clockedOut: "● uitgeklokt",
      notIn: "niet ingeklokt",
      later: "Uren aanpassen (met reden) en het overzicht per week of maand volgen in een volgende stap.",
      off: "In- en uitklokken staat uit. De knop ⏱ Klok is verborgen op alle toestellen.",
      turnedOn: "In- en uitklokken staat aan",
      turnedOff: "In- en uitklokken staat uit",
    },
  },
  alerts: {
    cancelled: (amount: string, paid: string, reason: string | null) =>
      `Bestelling geannuleerd (${amount}, ${paid})${reason ? ` · reden: ${reason}` : ""}`,
    unpaid: "onbetaald",
    methodChanged: (from: string, to: string) => `Betaalwijze gewijzigd: ${from} → ${to}`,
    // prototype addAlert: "Koerier Mehmet: Klant niet thuis"
    problem: (courier: string, reason: string) => `Koerier ${courier}: ${reason}`,
    qrNotBooked: (amount: string) =>
      `Bank-QR ${amount} is betaald (bevestigd door Bancontact) maar staat op geen enkele bestelling. Kijk na of de klant twee keer betaalde.`,
    removed: (items: string, amount: string) => `Product verwijderd na bestelling: ${items} (${amount})`,
    // a voucher shown after paying (Furkan, 14/10/2026): no product removed, money back all the same
    discounted: (label: string, amount: string) => `Korting na bestelling: ${label} (${amount})`,
    discountToo: (label: string) => `korting ${label}`,
    lowered: (amount: string) => `Bestelling verlaagd na bestelling (${amount})`,
    refund: (amount: string, method: string, cash: boolean) =>
      cash ? `${amount} contant teruggegeven` : `${amount} teruggestort via ${method}`,
  },
  delivery: {
    fee: "Leveringskosten",
    free: "gratis",
    receipt: "Bezorgkosten",
    notHere: (zone: string) => `${zone}: hier wordt niet geleverd`,
    minimum: (zone: string, amount: string) => `Minimum voor ${zone}: ${amount}`,
    outside: (street: string, km: string) => `Buiten leveringsgebied: ${street} ligt verder dan ${km} km`,
    closed: "Buiten de bezorgtijden",
  },
  /** The customer on the ticket (prototype renderCust and custCard). */
  customer: {
    phone: "Telefoon + Enter",
    /** A phone has no Enter on its number keys; a known number fills in the customer by itself. */
    phoneTouch: "Telefoon",
    phoneLabel: "Telefoonnummer",
    fromPlatform: (orders: number, platform: OrderPlatform) =>
      `Bestelde ${String(orders)}× via ${PLATFORM_NAMES[platform].full}: naam en adres ingevuld, kijk ze na.`,
    name: "Naam",
    email: "E-mail (optioneel, voor bevestiging)",
    emailLabel: "E-mail klant",
    zone: "Gemeente / deelgemeente (typ letters of postcode)",
    zoneLabel: "Gemeente of deelgemeente",
    street: "Straat",
    streetIn: (zone: string) => `Straat in ${zone}`,
    number: "Nr",
    numberLabel: "Huisnummer",
    box: "Bus",
    chooseZoneFirst:
      "Kies eerst zelf de gemeente of deelgemeente. De straatlijst toont dan enkel straten uit die gemeente.",
    zoneLine: {
      minimum: (amount: string) => `minimum ${amount}`,
      fee: (amount: string) => `levering ${amount}`,
      freeFrom: (amount: string) => `gratis vanaf ${amount}`,
      km: (km: string) => `± ${km} km`,
      extra: (amount: string) => `toeslag afstand ${amount}`,
      outside: "buiten leveringsgebied",
    },
    note: "Opmerking bij deze bestelling",
    noteHint: "enkel deze keer · komt op de bon",
    notePlaceholder: "bv. extra warm leveren, bel niet aan",
    unknown: "Onbekend nummer. Druk",
    unknownAfter: "om als nieuwe klant op te slaan.",
    intern: "Klantnotitie",
    internHint: "intern · klant ziet dit nooit · blijft bewaard",
    internPlaceholder: "bv. volgende keer gratis cola meegeven",
    incomplete: "Onvolledig telefoonnummer",
    saved: (phone: string) => `Nieuwe klant opgeslagen: ${phone}`,
    card: {
      label: "Klantenfiche",
      noName: "Naam onbekend",
      regular: "vaste klant",
      new: "nieuwe klant",
      orders: "bestellingen",
      average: "gemiddeld",
      since: (month: string) => `klant sinds ${month}`,
      points: (points: number, goal: number) => `${points} van ${goal} spaarpunten`,
      ready: (amount: string) => ` · ${amount} korting klaar`,
      last: (day: string) => `Laatste (${day}): `,
      repeat: "Herhaal",
    },
    repeated: "Laatste bestelling toegevoegd",
    repeatedPart: (count: number) =>
      `Laatste bestelling toegevoegd · ${count === 1 ? "1 product is" : `${count} producten zijn`} niet meer te koop`,
  },
  /** Nummerherkenning: the call on the ticket, the Bellerslijst (F10) and the device in Beheer. */
  calls: {
    unknown: "onbekend nummer",
    orders: (count: number) => `${count} bestellingen`,
    take: "Overnemen",
    skip: "Negeren",
    toast: (phone: string, name: string | null) => `Oproep: ${phone}${name ? ` · ${name}` : ""}`,
    test: "☎ Test oproep",
    testTitle: "Simuleert een oproep via nummerherkenning (USB of netwerk)",
    list: "Bellerslijst",
    columns: { time: "Tijd", number: "Nummer", customer: "Klant" },
    listCustomer: (name: string, orders: number, platform?: OrderPlatform) =>
      `${name} · ${orders} best.${platform ? ` via ${PLATFORM_NAMES[platform].short}` : ""}`,
    listUnknown: "onbekend",
    empty: "Nog geen oproepen. Nummers komen automatisch binnen via het nummerherkenningstoestel (USB of netwerk).",
    /** Beheer › Printers & toestellen › Nummerherkenning */
    setup: {
      note: "Een oproep verschijnt meteen op elke kassa: ☎ nummer · klant · aantal bestellingen, met Overnemen of Negeren. De bellerslijst opent met F10.",
      usb: "USB-toestel aan de telefoonlijn",
      usbWhere: "op deze kassa-pc",
      network: "Netwerk (VoIP-centrale of IP-toestel)",
      network2: "voor alle kassa's",
      status: {
        unsupported: "Deze browser kan geen USB-toestel lezen: gebruik Chrome of Edge op de kassa-pc.",
        off: "Niet gekoppeld",
        connecting: "Verbinden…",
        on: "Verbonden: het toestel antwoordt",
        silent: "Gekoppeld, maar het toestel antwoordt niet",
        absent: "Gekoppeld, maar niet aangesloten",
        busy: "In gebruik door een ander programma (bv. de vorige kassa). Sluit dat programma en kies opnieuw.",
      },
      choose: "Toestel kiezen",
      log: "Wat het toestel doorstuurt",
      logNote:
        "→ is wat Tafo vraagt, ← is wat het toestel antwoordt. Bel naar de zaak: je moet RING zien en daarna een regel met het nummer (NMBR = …). Zie je RING zonder nummer, bel dan opnieuw: na elke oproep zonder nummer probeert Tafo vanzelf een andere instelling (vijf in totaal) en onthoudt de instelling die werkt.",
      unlink: "Ontkoppelen",
      unlinked: "Nummerherkenning ontkoppeld op deze kassa",
      networkOff: "Uit",
      networkOn: (day: string) => `Aan sinds ${day}`,
      makeAddress: "Webadres maken",
      newAddress: "Nieuw webadres",
      switchOff: "Uitzetten",
      switchedOff: "Nummerherkenning via het netwerk staat uit",
      addressNote:
        "Vul dit webadres in bij je telefooncentrale of IP-toestel, bij een inkomende oproep, met het nummer erachter: …?number=0472184490 (bij Yealink en Snom: number=$remote). Het adres werkt als een wachtwoord: het wordt maar één keer getoond.",
      copy: "Kopiëren",
      copied: "Webadres gekopieerd",
    },
  },
  codes: {
    entered: (count: number, length: number) => `${count} van ${length} cijfers`,
    backspace: "Laatste cijfer wissen",
    free: "Gratis geven",
    discount: "Korting geven",
    editPaid: "Betaalde bestelling bewerken",
    cancel: "Bestelling annuleren",
    correct: "Foutieve bestelling annuleren",
    delete: "Bestelling verwijderen",
    // Takeaway and Uber Eats: only the owner (Furkan, 17/10/2026)
    platformMethod: "Betaalwijze Takeaway / Uber Eats wijzigen",
    platformPaidOnline: "Online betaald op Takeaway / Uber Eats",
    acceptTerms: "Voorwaarden aanvaarden",
    beheer: "Beheer openen",
    beheerAgain: "Je code opnieuw, om verder te werken in Beheer",
    adminCode: "Speciale admin code",
    adminCodeMissing: "Vraag je speciale admin code aan Tafo, neem contact op.",
    by: (name: string) => `door ${name}`,
  },
  /**
   * Tafo-beheer: a fresh code of the operator's authenticator app before what hands out power over every shop,
   * publishing Tafo voor Windows and giving someone access (security review, 30/10/2026). The server's refusals
   * of the code by their code.
   */
  freshCode: {
    title: "Bevestigen met je authenticator-app",
    label: "Code van 6 cijfers",
    hint: "Elke code werkt één keer. Net aangemeld of de code al gebruikt? Wacht dan op de volgende code van de app.",
    format: "De code van de authenticator-app heeft 6 cijfers.",
    cancel: "Annuleren",
    errors: {
      two_factor_required: "Typ de code van je authenticator-app.",
      two_factor_wrong: "Deze code klopt niet of werd al gebruikt. Typ de volgende code van je authenticator-app.",
      too_many_login_attempts: "Te veel foute codes. Probeer het over 15 minuten opnieuw.",
    },
  },
  errors: {
    network: "Geen verbinding met de server",
    server: "De server heeft een probleem. Probeer het zo meteen opnieuw.",
    unexpected: "Er ging iets mis",
    connection: "Verbinding met de server verbroken. De kassa probeert opnieuw…",
    unconfirmed: (count: number) =>
      count === 1 ? "1 verkoop is nog niet bevestigd door de server" : `${count} verkopen zijn nog niet bevestigd`,
    retryNow: "Nu opnieuw proberen",
    menuMissing: "Er is nog geen menu. De eigenaar importeert het menu in het beheer.",
    shopInactive: "Deze zaak is gepauzeerd. Neem contact op met Tafo.",
    isApp: (url: string) => `Dit toestel is gekoppeld als Tafo-app (ober of koerier). Open de app op ${url}.`,
    screenTitle: "Dit scherm kon niet getoond worden",
    screenHint:
      "De kassa en de server hebben mogelijk een verschillende versie (na een update). Herlaad de kassa; verkopen gaan niet verloren.",
    reload: "Kassa herladen",
  },
  pairing: {
    title: "Kassa koppelen",
    intro: "Vraag de eigenaar om een koppelcode (Beheer → Toestellen → Nieuw toestel) en typ ze hieronder.",
    code: "Koppelcode",
    submit: "Koppelen",
    busy: "Bezig…",
  },
  terms: {
    // the acceptance screen of the prototype (openAkkGate) and the in-app texts (Deel D a2)
    title: "Algemene voorwaarden en GDPR",
    subtitle: {
      first: "Om Tafo te gebruiken moet de eigenaar de voorwaarden aanvaarden. Zonder akkoord kan niemand verder.",
      reaccept: (version: string, from: string) =>
        `Nieuwe versie ${version} geldt sinds ${from}. De eigenaar moet ze opnieuw aanvaarden.`,
      new: (version: string, from: string) => `Nieuwe versie ${version} vanaf ${from}.`,
    },
    whatChanges: "Wat verandert:",
    readHint:
      "Lees de volledige tekst tot het einde: algemene voorwaarden, verwerkersovereenkomst (GDPR) en privacyverklaring",
    readDone: "✓ Volledig gelezen. Vink aan en bevestig met de code van de eigenaar.",
    confirmTerms: (firm: string, version: string) =>
      `Ik ben eigenaar of zaakvoerder van ${firm}. Ik aanvaard de Algemene voorwaarden en de Verwerkersovereenkomst (GDPR) versie ${version} en heb de privacyverklaring gelezen.`,
    confirmOnline:
      "Tafo werkt enkel met internet. Valt internet of stroom uit, dan werkt de kassa niet; ik voorzie een noodprocedure.",
    name: "Naam van de eigenaar",
    noOwner: "Geen eigenaar? Vraag de eigenaar om de link in zijn mailbox te openen of hier zijn code in te geven.",
    linkSentAt: (when: string) => `Akkoordlink verstuurd op ${when}.`,
    sendLink: (again: boolean, email: string) => `✉ Akkoordlink ${again ? "opnieuw " : ""}naar ${email}`,
    linkSent: (email: string) => `Akkoordlink gemaild naar ${email}`,
    accept: "Aanvaarden met eigenaarscode",
    accepted: (email: string) => `Akkoord bewaard · bevestiging met PDF automatisch gemaild naar ${email}`,
    documents: { terms: "Algemene voorwaarden", dpa: "Verwerkersovereenkomst", privacy: "Privacyverklaring" },
    version: (version: string) => `versie ${version}`,
    close: "Sluiten",
    notice: {
      // prototype openAkkNotice: once per start-up while a new version is announced
      title: (from: string) => `Nieuwe voorwaarden vanaf ${from}`,
      subtitle: (version: string) => `versie ${version} · ook per e-mail naar de eigenaar`,
      reaccept: (from: string) =>
        `Aanvaard de nieuwe versie vóór ${from}. Daarna kan de kassa pas weer gebruikt worden na het akkoord van de eigenaar.`,
      nothing: "U hoeft niets te doen.",
      cancel: (from: string) => `Niet akkoord? U kunt kosteloos opzeggen vóór ${from}.`,
      later: "Later",
      read: "Lezen en aanvaarden",
    },
    mail: {
      title: "Bevestigingsmail",
      sent: (when: string) => `automatisch verstuurd op ${when}`,
      to: "Aan",
      bcc: "Bcc",
      subject: "Onderwerp",
      attachments: "Bijlagen",
    },
  },
  general: {
    title: "Algemeen",
    saved: "Opgeslagen · meteen actief",
    shop: {
      title: "Zaakgegevens",
      receiptName: "Naam op de bon",
      displayName: "Naam in mails en website",
      company: "Vennootschap",
      street: "Straat en nummer",
      postcode: "Postcode",
      city: "Gemeente",
      phone: "Telefoon",
      email: "E-mail",
      emailPlaceholder: "bv. info@papermoon.be",
      vatNumber: "Btw-nummer",
      vatPlaceholder: "BE0000.000.000",
      iban: "IBAN (voor bank-QR in de app)",
      ibanPlaceholder: "BE00 0000 0000 0000",
    },
    errors: {
      receiptName: "Naam op de bon mag niet leeg zijn.",
      displayName: "Vul een naam in.",
      street: "Vul straat en nummer in.",
      postcode: "Postcode = 4 cijfers.",
      city: "Vul de gemeente in.",
      email: "Geen geldig e-mailadres.",
      vatNumber: "Btw-nummer als BE0755.336.723.",
      iban: "Dit rekeningnummer klopt niet (controlecijfer). Kijk het na.",
      minutes: "Minuten tussen 0 en 240",
    },
    hours: {
      title: "Openingsuren van de zaak",
    },
    prep: {
      title: "Bereidingstijd",
      takeaway: "Afhaal klaar na",
      delivery: "Levering aan huis na",
      minutes: "min",
      note: "Komt op de bon (afhaal- of bezorgtijd) en in de bevestigingsmail.",
    },
    receipt: {
      title: "Kassabon",
      showHours: "Openingsuren op de bon",
      header: "Extra regels bovenaan",
      headerHint: 'één per regel, bv. website of "Ook op MyMenu"',
      footer: "Tekst onderaan",
      footerHint: "één per regel",
      note: 'Onderaan elke bon staat klein het Tafo-logo met "Kassa door Tafo · tafo.be".',
    },
    // the "Korting" buttons of the checkout, chosen by the shop (Furkan, 13/10/2026)
    discounts: {
      title: "Kortingen bij het afrekenen",
      new: "Nieuwe korting",
      placeholder: "bv. 4 of 10%",
      add: "+ Toevoegen",
      remove: (label: string) => `${label} verwijderen`,
      none: "Geen kortingen: in het afrekenvenster staat dan geen Korting.",
      invalid: "Typ een bedrag in euro (4 of 0,50) of een percentage (10%).",
      exists: (label: string) => `${label} staat al in de lijst`,
      full: (max: number) => `Hoogstens ${String(max)} kortingen`,
    },
    qr: {
      title: "Betalen met bank-QR in de Tafo-app",
      courier: "Koerier mag bank-QR tonen aan de deur",
      waiter: "Ober mag bank-QR tonen aan tafel",
      tips: "Fooi vragen bij afrekenen aan tafel (ober-app): 5, 10, 15% of geen",
      /** Furkan, 09/10/2026: no orders and no app outside the hours, nor until 30 minutes before the opening. */
      appHours: "Ober- en koerier-app enkel tijdens de openingsuren (vanaf 30 minuten voor de opening)",
      noIban: "Er is nog geen IBAN bij de zaakgegevens",
      noIbanAfter: ": vraag Tafo om het in te vullen, anders werkt de QR niet.",
      bancontact: {
        title: "Bancontact Pro QR op het scherm",
        key: "API-sleutel",
        placeholder: "Plak hier de sleutel",
        save: "Sleutel opslaan",
        replace: "Sleutel vervangen",
        remove: "Sleutel verwijderen",
        checking: "Bancontact kijkt de sleutel na…",
        on: (date: string, by: string) => `✓ Actief sinds ${date} · ingesteld door ${by}`,
        hidden: "De sleutel is bewaard en wordt nooit meer getoond. Een nieuwe sleutel vervangt de oude.",
        saved: "Sleutel van Bancontact Pro bewaard · de bank-QR bevestigt nu zichzelf",
        removed: "Sleutel verwijderd · de bank-QR bevestig je weer zelf",
        removeConfirm:
          "De sleutel van Bancontact Pro verwijderen? De bank-QR wordt dan weer de QR van je bank: de ober of de koerier kijkt zelf of de klant betaald heeft.",
        /** Under "Speciale admin code" before a key or a product is saved or removed (security review, 30/10/2026). */
        adminCode: "Dit bepaalt op welke rekening je klanten betalen.",
      },
      /** Bancontact Pro QR on the receipt (ADR 0029). */
      receipt: {
        title: "Bancontact Pro QR op de bon",
        productId: "Product-ID",
        productPlaceholder: "Plak hier de product-ID",
        save: "Opslaan",
        replace: "Vervangen",
        remove: "Verwijderen",
        hidden:
          "De sleutel is bewaard en wordt nooit meer getoond. Bij “Bancontact Pro QR” op de kassa drukt Tafo nu de QR op de bon.",
        saved: "Bancontact Pro QR op de bon staat aan",
        removed: "Bancontact Pro QR op de bon staat uit",
        removeConfirm:
          "Bancontact Pro QR op de bon uitzetten? Bonnen die al gedrukt zijn kan de klant nog scannen, maar Tafo ziet die betalingen dan niet meer binnenkomen.",
        callback:
          "Plak dit webadres bij “Callback-URL” terwijl je het product bij Bancontact aanmaakt: achteraf kan je het daar niet meer invullen. Dan weet Tafo meteen dat er betaald is; zonder vraagt Tafo het zelf om de paar seconden. Het adres is van jouw zaak, blijft altijd hetzelfde en werkt als een wachtwoord: geef het aan niemand anders.",
        callbackLabel: "Webadres voor Bancontact Pro",
        copy: "Kopiëren",
        copied: "Webadres gekopieerd",
      },
    },
    drawer: {
      title: "Kassalade",
      float: "Wisselgeld bij de start van de dag €",
      saved: (amount: string) => `Wisselgeld ${amount}`,
    },
    preview: "Voorbeeld bon (kop en voet)",
    previewOrder: "… bestelling …",
  },
  subscription: {
    title: "Abonnement & facturen",
    /** The payment at start-up (Furkan, 26/10/2026: "als het programma opengaat moet het systeem om betaling vragen"). */
    pay: {
      titleDue: "Je Tafo-licentie loopt bijna af",
      titleExpired: "Je Tafo-licentie is verlopen",
      blocked: "Betaal de licentie om de kassa weer te gebruiken.",
      forMonth: (month: string) => `Licentie ${month}`,
      /** Paid at Mollie only (Furkan, 29/10/2026): without Tafo's key there, whom to ask; never an account. */
      onlineOff: "Online betalen staat nog niet aan. Neem contact op met Tafo: info@tafo.be.",
      later: "Later betalen",
      retry: "Opnieuw controleren",
      onlineBusy: "Betaal-QR van Bancontact laden…",
      onlineScan: "Scan met de app van je bank",
      onlineQr: "QR-code van Bancontact",
      other: "Andere betalingsmogelijkheden",
      otherBusy: "Betaalpagina openen…",
      via: "Veilig betalen via",
      bar: "Je licentie loopt af · Betalen",
      barShort: "Licentie betalen",
      barHint: (days: number) =>
        days <= 1
          ? "Je Tafo-licentie loopt vandaag af: tik om te betalen"
          : `Je Tafo-licentie loopt af over ${String(days)} dagen: tik om te betalen`,
      waiting: "Zodra de betaling bevestigd is, werkt de kassa vanzelf verder.",
      newQr: "Nieuwe QR-code",
      tryAgain: "Opnieuw proberen",
      stuck: "Lukt het niet? Neem contact op met Tafo: info@tafo.be.",
      paidTitle: "Betaling ontvangen",
      paidThanks: "Bedankt! Je Tafo-licentie is in orde.",
      paidUntil: (day: string) => `Je licentie loopt nu tot en met ${day}.`,
      paidInvoice: "De factuur komt in je mailbox.",
      close: "Sluiten",
      toTill: "Verder naar de kassa",
    },
    plan: {
      title: "Tafo-abonnement",
      package: "Pakket",
      packageName: "Tafo-abonnement",
      price: "Prijs",
      priceText: (price: string, vat: number, monthly: string) =>
        `${price} + ${String(vat)}% btw = ${monthly} per maand`,
      paidUntil: "Betaald tot en met",
      status: "Status",
      ok: "In orde",
      expired: "Verlopen",
      grace: (until: string) => `Uitstel tot ${until}`,
      due: (days: number, month: string) =>
        `Nog ${String(days)} ${days === 1 ? "dag" : "dagen"} · betaal vóór 1 ${month}`,
      // the ⏳ line (prototype licDaysText)
      graceText: "Je licentie is verlopen, maar je kreeg uitzonderlijk uitstel tot ",
      expiredText: "Je Tafo-licentie liep af op ",
      lastDay: (end: string) => `Vandaag is de laatste dag van je licentie (${end}).`,
      daysLeft: "Je licentie loopt nog ",
      days: (days: number) => `${String(days)} dagen`,
      until: (end: string) => `, tot en met ${end}.`,
      note: (day: number) =>
        `Vooraf te betalen vóór de ${String(day)}e van elke maand. Enkele dagen vooraf vraagt de kassa bij het opstarten om de betaling, met een QR-code voor je bank-app.`,
    },
    invoices: {
      title: "Facturen",
      columns: { document: "Document", date: "Datum", period: "Periode", paidWith: "Betaald met", amount: "Bedrag" },
      creditNote: "creditnota",
      view: "Bekijken",
      pdf: "⬇ PDF",
      none: "Nog geen facturen.",
      downloaded: (file: string) => `Factuur opgeslagen: ${file}`,
    },
    contract: {
      title: "Contract",
      accepted: (version: string, name: string, when: string, via: string) =>
        `✓ Akkoord v${version} door ${name} op ${when} via ${via}`,
      via: { email_link: "akkoordlink", device_owner_pin: "opstart kassa met eigenaarscode" },
      notAccepted: "Je hebt de voorwaarden van Tafo nog niet aanvaard.",
      upcoming: (version: string, from: string, summary: string, reaccept: boolean) =>
        `📄 Nieuwe voorwaarden versie ${version} vanaf ${from}: ${summary} ${
          reaccept ? "Aanvaard vóór die datum, anders werkt de kassa dan niet meer." : "Je hoeft niets te doen."
        }`,
      readAndAccept: "Lezen en aanvaarden",
      proof: (who: string, device: string | null, ip: string | null, version: string, when: string) =>
        `Bewijs: ${who} · ${device ?? "—"} · IP ${ip ?? "—"} · bevestiging met PDF (voorwaarden, verwerkersovereenkomst, privacyverklaring v${version}) automatisch gemaild op ${when} (kopie naar het Tafo-archief) · `,
      byOwnerCode: (account: string) => `eigenaarscode van ${account} op de kassa`,
      byLink: (email: string) => `link geopend vanuit ${email}`,
      viewMail: "Bevestigingsmail bekijken",
    },
    invoice: {
      invoice: "Factuur",
      creditNote: "Creditnota",
      number: "Nummer",
      date: "Datum",
      due: "Vervaldag",
      forInvoice: "Bij factuur",
      customer: "Klant",
      to: "Factuur aan",
      vat: (number: string) => `BTW ${number}`,
      iban: (iban: string) => `IBAN ${iban}`,
      shop: (name: string) => `Zaak: ${name}`,
      description: "Omschrijving",
      period: "Periode",
      net: "Excl. btw",
      vatRate: (rate: number) => `Btw ${String(rate)}%`,
      total: "Totaal",
      totalNet: "Totaal excl. btw",
      crediting: "Creditering ",
      reason: (reason: string, refund: boolean) => `Reden: ${reason}${refund ? " · wordt teruggestort" : ""}`,
      paid: (date: string, method: string) => `✓ Betaald op ${date} · ${method}`,
      foot: (reference: string) =>
        `Gestructureerde mededeling ${reference} · Vragen? Antwoord gewoon op de mail bij deze factuur.`,
      close: "Sluiten",
      download: "⬇ PDF downloaden",
    },
    months: [
      "januari",
      "februari",
      "maart",
      "april",
      "mei",
      "juni",
      "juli",
      "augustus",
      "september",
      "oktober",
      "november",
      "december",
    ],
  },
  sale: {
    oneSize: (category: string) => `${category} · één formaat`,
    channels: { takeaway: "Afhaal", delivery: "Levering", table: "Tafel" } satisfies Record<Channel, string>,
    /** The bar of the folded customer block: the kind of order and the customer. */
    customerBar: {
      nobody: "Geen klantgegevens",
      noAddress: "Adres nog invullen",
      show: "Klantgegevens tonen",
      hide: "Klantgegevens inklappen",
    },
    table: "Tafel",
    /** The empty ticket (prototype renderLines). */
    empty: ["Nog niets besteld.", "Tik een product of typ een code, bv. ", "2DP", " of ", "PEPM", "."],
    /** A phone: no codes typed on a keyboard (Furkan, 18/10/2026). */
    emptyTouch: ["Nog niets besteld.", "Tik een product in Menu."],
    code: "Code",
    codePlaceholder: "bv. 2DP of PEPM",
    codeHint: "Code intikken bij F3 (de code staat in Beheer › Menu en verschijnt als je over een tegel beweegt)",
    codeUnknown: (code: string) => `Code "${code}" bestaat niet`,
    tileCode: (code: string) => `Code ${code}`,
    park: "Parkeren",
    minus: "−1",
    plus: "+1",
    free: "Gratis",
    copy: "Kopie",
    remove: "Verwijder",
    subtotal: "Subtotaal",
    discount: "Korting",
    vat: (rate: number) => `Btw ${rate}%`,
    total: "Totaal",
    newOrder: "Nieuw",
    checkout: "Afrekenen",
    /** A phone: the sale in two tabs, the menu or the order with its number of products (Furkan, 18/10/2026). */
    paneMenu: "Menu",
    paneOrder: (count: number) => (count === 0 ? "Bestelling" : `Bestelling (${String(count)})`),
    panes: "Menu of bestelling",
    saveOpen: { table: "Naar keuken", delivery: "Bestellen", takeaway: "Bestellen" } satisfies Record<Channel, string>,
    saveOpenHint: {
      table: "Later afrekenen",
      delivery: "Betalen aan de deur",
      takeaway: "Later afrekenen",
    } satisfies Record<Channel, string>,
    saved: (code: string) => `Bestelling ${code} opgeslagen`,
    missing: {
      table: "Kies eerst een tafel",
      zone: "Kies eerst de gemeente of deelgemeente",
      street: "Vul de straat in",
    },
    freeLabel: "gratis",
    noPrices: "Kies een categorie",
    allergens: "⚠ Allergenen",
    allergensTitle: "Allergenen per product, voor personeel",
  },
  /** The receipt right after a delivery (prototype openReceipt). */
  receiptDialog: {
    title: "Kassabon",
    qr: "De QR-code opent Google Maps met route naar dit adres (ook op iPhone).",
    testLink: "Test de link",
    setup:
      "Kop (naam, adres, BTW-nummer), openingsuren en voettekst stelt de zaak zelf in. BTW-letters: A 21%, B 12%, C 6%.",
  },
  /** "⚠ Allergenen" on the sale screen, for the staff (prototype openAlgCard). */
  allergenCard: {
    title: "⚠ Allergenen",
    search: "Zoek product of code…",
    searchLabel: "Zoek product",
    allergic: "Klant is allergisch voor…",
    allowMaybe: "ook 'mogelijk' toelaten",
    count: (count: number) => (count === 1 ? "1 product" : `${String(count)} producten`),
    countWithout: (count: number, allergens: string, maybe: boolean) =>
      `${count === 1 ? "1 product" : `${String(count)} producten`} zonder ${allergens}${maybe ? " (behalve 'mogelijk')" : ""}`,
    none: "Geen producten gevonden.",
    proposal: "voorstel, nog niet bevestigd",
    foot: "Keuzes zoals sauzen en extra's kunnen allergenen toevoegen. Vraag bij twijfel in de keuken.",
    /** The folder of the shop, from here (Furkan, 21/10/2026: "met ! toets vanuit menu"). */
    folder: "Folder bewerken",
    folderTitle: "De folder van de zaak openen om teksten en prijzen aan te passen en te laten drukken",
    folderCode: "Folder bewerken",
    folderBlocked: "De folder kon niet openen: laat vensters toe voor de kassa, of maak hem in Beheer › Menu › Folder.",
  },
  /** De Wachtlijst (prototype renderParked): tickets put aside with Parkeren, on every device. */
  /** De Live-kaart (Furkan, 09/10/2026): waar elke koerier op rit is, zijn adressen en wanneer hij terug is. */
  liveMap: {
    title: "Live-kaart",
    region: "Kaart met de koeriers",
    shop: "Zaak",
    showAll: "Alles tonen",
    zoomIn: "Inzoomen",
    zoomOut: "Uitzoomen",
    noCouriers: "Geen koerier onderweg",
    returning: "↩ terug naar de zaak",
    noCouriersNote: "Zodra een koerier vertrekt met de Tafo-app, zie je hier waar hij is.",
    live: (ago: string) => `● live · ${ago}`,
    lastSeen: (ago: string) => `laatst gezien ${ago}`,
    ago: (seconds: number) =>
      seconds < 60 ? "nu" : seconds < 3600 ? `${String(Math.floor(seconds / 60))} min geleden` : "lang geleden",
    noGps: "geen gps · schatting",
    notLeft: "nog niet vertrokken",
    back: (time: string) => `Terug ± ${time}`,
    backIn: (minutes: number) => (minutes <= 0 ? "nu" : `over ${String(minutes)} min`),
    stop: (code: string, address: string) => `${code} · ${address}`,
    at: (time: string) => `± ${time}`,
    waiting: (count: number) => `Te leveren (${String(count)})`,
    /** The deliveries nobody took yet on the map (Furkan, 25/10/2026). */
    showOrders: (count: number) => `Bestellingen tonen (${String(count)})`,
    hideOrders: "Bestellingen verbergen",
    noPlace: "straat nog niet op de kaart",
    noMap: "Geen kaart: de plaats van de zaak is nog niet gekend (Beheer › Algemeen, adres).",
    privacy:
      "De plaats van een koerier komt enkel tijdens een rit binnen, tot hij terug in de zaak is. De klant ziet ze nooit.",
  },
  couriers: {
    title: "Koeriers",
    // a problem at the door, from the Tafo app (blok J)
    problemToast: (courier: string, reason: string, code: string, address: string) =>
      `⚠ Koerier ${courier}: ${reason} · ${code}${address === "" ? "" : ` · ${address}`}`,
    problemOnCard: (reason: string, time: string) => `⚠ ${reason} · ${time}`,
    modeLabel: "Werkwijze voor deze zaak:",
    modes: { self: "Koerier neemt zelf in app", till: "Kassa wijst toe" } satisfies Record<CourierMode, string>,
    modeCode: "Werkwijze koeriers",
    modeChanged: (mode: string) => `Werkwijze: ${mode}`,
    toDeliver: "Te leveren",
    nothing: "Niets",
    noRides: "Geen ritten",
    onTheWay: (count: number) => `🛵 onderweg (${String(count)})`,
    ready: "klaar om te vertrekken",
    inShop: "in de zaak",
    returning: (time: string) => `↩ terug ± ${time}`,
    signedIn: " · ingelogd in app",
    km: (km: string) => `± ${km} km`,
    paid: (methods: string) => `Betaald · ${methods}`,
    due: (amount: string) => `Te innen ${amount}`,
    arrival: (time: string) => `onderweg · aankomst ± ${time}`,
    assignTo: (name: string) => `→ ${name}`,
    back: "Terug",
    delivered: "Geleverd",
    track: "Volgpagina klant",
    settleBar: (count: number, amount: string) => `${String(count)} geleverd · contant ${amount}`,
    bon: "🧾 Bon",
    settle: "Afrekenen",
    assigned: (name: string) => `Toegewezen aan ${name}`,
    deliveredToast: (code: string) => `Geleverd · ${code}`,
    noteSelf: "Koeriers zien de klare bestellingen in hun app en nemen ze zelf. De kassa kan altijd bijsturen.",
    noteTill: "Het personeel wijst elke levering toe; de koerier ziet enkel zijn eigen ritten.",
    noteSettle:
      " Afrekenen per dienst: druk de afrekenbon af, de koerier maakt zijn geld klaar en geeft het af; een manager of de eigenaar bevestigt met zijn code.",
    appNote: ["De koerier werkt met de ", "Tafo-app", " op zijn gsm."] as const,
    today: "Afrekeningen vandaag",
    columns: {
      time: "Tijd",
      courier: "Koerier",
      rides: "Ritten",
      cash: "Contant afgegeven",
      by: "Afgerekend door",
    },
    reprint: (name: string, time: string) => `Afrekenbon ${name} van ${time}`,
    settleCode: "Koerier afrekenen",
    // corrections with a code (Furkan, 11/10/2026: settled by mistake, the wrong courier)
    undo: "Ongedaan maken",
    undoOf: (name: string, time: string) => `Afrekening ${name} van ${time} ongedaan maken`,
    undoCode: "Afrekening ongedaan maken",
    undone: "ongedaan",
    undoneBy: (by: string, time: string, reason: string) => `ongedaan door ${by} om ${time}: ${reason}`,
    undoDialog: {
      title: (name: string, time: string) => `Afrekening ${name} van ${time} ongedaan maken`,
      note: "De ritten komen terug bij de koerier om opnieuw af te rekenen. De afgedrukte afrekening blijft bewaard, met wie ze ongedaan maakte en waarom.",
      reason: "Reden",
      placeholder: "bv. per vergissing afgerekend",
      cancel: "Annuleren",
      confirm: "Ongedaan maken",
      done: (name: string) => `Afrekening ${name} ongedaan gemaakt`,
    },
    settleDialog: {
      title: (name: string) => `Afrekenen · ${name}`,
      columns: {
        code: "Code",
        delivered: "Geleverd",
        address: "Adres",
        payment: "Betaling",
        amount: "Bedrag",
        courier: "Koerier",
      },
      otherCourier: (code: string) => `Andere koerier voor ${code}`,
      moved: (code: string, name: string) => `${code} → ${name}`,
      moveNote: "Bij de verkeerde koerier? Kies bij die rit een andere koerier: de rit en het geld gaan mee.",
      nothingLeft: (name: string) => `${name} heeft geen geleverde ritten meer om af te rekenen`,
      rides: "Ritten",
      cash: "Contant te ontvangen",
      other: "Bank-QR / vooraf betaald",
      by: "Afgerekend door",
      note: (amount: string) => `. De koerier geeft ${amount} contant af; de afrekenbon wordt afgedrukt en bewaard.`,
      cancel: "Annuleren",
      confirm: "Afrekenen",
      done: (name: string) => `${name} afgerekend`,
    },
    bonDialog: {
      title: (name: string) => `Afrekenbon · ${name}`,
      heading: "AFREKENING KOERIER",
      settled: "afgerekend",
      pending: "nog af te rekenen",
      groups: { cash: "CONTANT ONTVANGEN", bank_qr: "BANK-QR", prepaid: "VOORAF BETAALD" } satisfies Record<
        SettleGroup,
        string
      >,
      subtotals: {
        cash: "Subtotaal contant",
        bank_qr: "Subtotaal bank-QR",
        prepaid: "Subtotaal vooraf",
      } satisfies Record<SettleGroup, string>,
      rides: "Aantal ritten",
      handOver: "AF TE GEVEN CONTANT",
      by: "Afgerekend door",
      copy: "Kopie van de afrekening.",
      pendingNote:
        "Afgedrukt zodat de koerier op zijn gemak kan controleren en zijn geld klaarmaken. Daarna rekent een manager of de eigenaar af met zijn code.",
      print: "🖨 Afdrukken",
    },
    trackDialog: {
      title: "Volg je bestelling",
      note: "Zo ziet de klant het na de link in de mail (ook op gsm). Geen app nodig.",
    },
  },
  parked: {
    title: "Wachtlijst",
    parked: "Geparkeerd. Geen bon naar de keuken.",
    empty:
      "Geen geparkeerde bestellingen. Druk op Parkeren (Pause) om een bestelling even opzij te zetten; ze is dan zichtbaar op alle toestellen.",
    noCustomer: "geen klant",
    by: (name: string) => `door ${name}`,
    resume: "Verder werken",
    drop: "Weggooien",
    dropped: "Weggegooid",
    busyTitle: "Verder werken",
    busy: "Op het ticket staat nog een bestelling. Die eerst parkeren en dan deze verder zetten?",
    parkAndResume: "Parkeren en verder werken",
    back: "Terug",
    channel: { takeaway: "afhaal", delivery: "levering" },
    // "Betalen bij afhalen" in the Tafo app (blok J)
    awaiting: "Te betalen bij afhalen",
    inKitchen: "al in de keuken",
    pay: "Afrekenen",
  },
  tables: {
    choose: "Kies tafel",
    other: "Andere tafel",
    move: "⇄ Tafel verplaatsen",
    whichToMove: "Welke tafel verplaatsen?",
    moveTo: (table: string) => `Tafel ${table} verplaatsen naar…`,
    busyCount: (count: number) => `${count} bezet`,
    free: "vrij",
    minutes: (minutes: number) => `${minutes} min`,
    note: "Oranje = bezet, met tijd aan tafel en openstaand bedrag. Een bezette tafel openen laat je bijbestellen of afrekenen. De indeling pas je aan in Beheer › Tafelplan.",
    // prototype; with printers "wat al in de keuken is … De keuken krijgt een melding." (blok I)
    moveNote: "Alles gaat mee: wat al besteld is, wat nog niet verstuurd is en de starttijd.",
    moveNoteKitchen:
      "Alles gaat mee: wat al in de keuken is, wat nog niet verstuurd is en de starttijd. De keuken krijgt een melding.",
    noFreeTable: "Geen vrije tafel.",
    moved: (from: string, to: string) => `Tafel ${from} verplaatst naar tafel ${to}`,
    movedKitchen: (from: string, to: string) => `Tafel ${from} verplaatst naar tafel ${to} · melding naar de keuken`,
    saved: (table: string) => `Tafel ${table} bewaard`,
    savedKitchen: (table: string) => `Tafel ${table} bewaard · bon naar keuken`,
    label: (table: string) => `Tafel ${table}`,
  },
  product: {
    size: "Formaat",
    without: "Zonder",
    /** On the receipt and the ticket for the "Groenten" button (prototype modsText). */
    allVegetables: "Zonder groenten",
    freeThen: (count: number, price: string) => `${String(count)} gratis, daarna ${price}`,
    required: "verplicht",
    choose: (count: number) => (count === 1 ? "kies 1" : `kies ${String(count)}`),
    /** A vegetable that "Groenten" takes away. */
    gone: "weg",
    allBut: (ingredient: string) => `Tik: alle groenten weg behalve ${ingredient}`,
    /** Next to "Zonder": the customer names what he does want; on the receipt "Enkel sla" (Furkan, 09/10/2026). */
    only: "Enkel",
    onlyTitle: "Enkel: tik wat de klant wél wil, de rest blijft weg",
    onlyHint: "tik wat erop mag",
    kitchenNote: "Opmerking voor keuken",
    add: "Toevoegen",
    update: "Bijwerken",
    close: "Sluiten",
  },
  checkout: {
    /** Bancontact Pro QR on the receipt (ADR 0029). */
    receiptQr: {
      printed: (amount: string) =>
        `Bon met Bancontact Pro QR gedrukt (${amount}). Tafo zet de bestelling zelf op betaald zodra de klant scant.`,
      whole: "Bancontact Pro QR is voor het hele bedrag dat nog open staat: wis eerst de deelbetaling.",
      paid: (code: string, amount: string) =>
        `✓ ${code} betaald met Bancontact Pro QR (${amount}) · bevestigd door Bancontact`,
      waiting: "wacht op Bancontact Pro QR",
      button: "Bancontact Pro QR op de bon",
      off: "Er komt geen QR op de bon: Bancontact Pro QR op de bon staat niet aan (Beheer › Algemeen). Bevestig enkel als de klant al betaald heeft.",
      onReceipt: "Betaal met Bancontact Pro",
      scan: (amount: string) => `Scan met je bank-app · ${amount}`,
      /** The same QR on the screen of a phone (Furkan, 18/10/2026). */
      screenWaiting:
        "Laat de klant dit scherm of de bon scannen met zijn bank-app. Tafo zet het op betaald zodra Bancontact bevestigt.",
      screenPaid: (amount: string) => `✓ Betaald · ${amount}`,
      screenClose: "Sluiten · de QR op de bon blijft geldig",
    },
    loyalty: (points: number, goal: number, amount: string) =>
      `Spaarkaart: ${String(points)}/${String(goal)} · ${amount} korting`,
    title: "Afrekenen",
    total: "Totaal",
    due: "Te betalen",
    paid: "Betaald",
    change: "Terug",
    discount: "Korting",
    noDiscount: "Geen",
    amountHint:
      "Bedrag leeg = rest betalen. Een deelbedrag intikken en daarna een betaalwijze kiezen = gesplitst betalen.",
    tooMuch: "Dit is meer dan er nog te betalen is",
    giveBackLive: "Terug geven",
    invalidAmount: "Dit bedrag klopt niet",
    confirmTerminal: (method: string, amount: string) => `${method}: ${amount} goedgekeurd op de terminal?`,
    confirmYes: "Ja, goedgekeurd",
    confirmNo: "Nee",
    sending: "Bezig met bevestigen…",
    done: "Betaald",
    giveChange: (amount: string) => `Geef ${amount} terug`,
    next: "Nieuwe bestelling",
    retry: "Opnieuw proberen",
    deferred: "De kassa verstuurt deze verkoop zodra de server bereikbaar is. Niet opnieuw aanrekenen.",
    unconfirmed:
      "De server heeft deze verkoop nog niet bevestigd. Probeer opnieuw: de kassa stuurt dezelfde verkoop, er wordt nooit twee keer aangerekend.",
  },
  orders: {
    title: "Bestellingen vandaag",
    empty: "Nog geen bestellingen vandaag",
    code: "Code",
    time: "Tijd",
    channel: "Soort",
    address: "Adres / klant",
    amount: "Bedrag",
    payment: "Betaling",
    status: "Status",
    statuses: { open: "open", paid: "betaald", cancelled: "geannuleerd" } satisfies Record<OrderStatus, string>,
    toDeliver: "te leveren",
    unpaid: "nog te betalen",
    online: "online",
    receipt: "Bon",
    print: "Afdrukken",
    // "de klant zegt achteraf: ik wil met QR betalen" (Furkan, 10/10/2026)
    printQr: "Met QR afdrukken",
    printQrTitle: "Opnieuw afdrukken met Bancontact Pro QR: de klant betaalt door te scannen",
    printQrPaid: (code: string, amount: string, methods: string) =>
      `${code} is al betaald (${amount}, ${methods}). Betaalt de klant toch met Bancontact Pro QR? Geef ${amount} terug aan de klant: de betaling wordt teruggedraaid en er komt een bon met QR voor ${amount}. Tafo zet de bestelling weer op betaald zodra de klant scant.`,
    printQrPaidConfirm: "Teruggeven en bon met QR afdrukken",
    edit: "Bewerken",
    changeMethod: "Betaalwijze",
    pay: "Afrekenen",
    cancel: "Annuleren",
    selectHint: "Kies een bestelling om de bon te zien",
    cancelTitle: "Bestelling annuleren?",
    cancelReason: "Reden",
    chooseReason: "Kies een reden",
    cancelReasons: [
      "Klant heeft geannuleerd",
      "Dubbele bestelling",
      "Uitverkocht",
      "Te druk in het restaurant",
      "Leveradres te ver",
      "Levering verhinderd door het weer",
      "Technisch probleem",
      "Restaurant vandaag gesloten",
      "Andere reden",
    ],
    whatHappens: "Dit gebeurt automatisch",
    nothingPaid: "Er is nog niets betaald.",
    giveBackCash: (amount: string) => `Er is ${amount} contant betaald: geef dit terug aan de klant.`,
    // prototype: for an open order the kitchen gets GEANNULEERD (when the shop has printers)
    ownerAndReports: "De zaakvoerder krijgt een melding, en de bestelling telt niet meer mee in het X- en Z-rapport.",
    ownerAndKitchen: "De zaakvoerder krijgt een melding, en de keuken krijgt een bon GEANNULEERD.",
    customerMailed: "De klant krijgt een mail met de reden.",
    customerUnknown: "De klant is niet gekend; er gaat geen bericht uit.",
    finalAfterCancel: "Een geannuleerde bestelling kan daarna niet meer gewijzigd worden.",
    back: "Terug",
    confirmCancel: "Bestelling annuleren",
    cancelledBy: (code: string, name: string) =>
      `Bestelling ${code} geannuleerd door ${name} · e-mail naar zaakvoerder verstuurd`,
    methodTitle: "Betaalwijze wijzigen",
    methodHint:
      "Bv. de klant betaalt toch met Bancontact of bank-QR in plaats van contant. Een code is niet nodig. Elke wijziging wordt bewaard (wanneer, van wat naar wat, op welke kassa) en staat in het Z-rapport.",
    // a Takeaway or Uber Eats order the bridge read wrong (Antalya, 17/10/2026)
    methodHintPlatform:
      "Kies hoe de klant echt betaald heeft. Naar online: de € 0,20 servicekosten van Takeaway gaan van het totaal af en de koerier rekent er geen cash voor af. Van online naar contant, Bancontact, QR of kaart aan de deur: de € 0,20 servicekosten van Takeaway komen bij het totaal (geen omzet van de zaak). Elke wijziging staat in de geschiedenis van de bestelling en in het logboek van de zaak, met de naam van de eigenaar.",
    paidOnline: "Online betaald",
    paidOnlineTitle: "Werd op Takeaway of Uber Eats al online betaald: de koerier int niets meer",
    paidOnlineDone: (code: string) => `${code} staat nu op online betaald`,
    adminTitle: "Bestellingen",
    /** Beheer › Bestellingen per dag (Furkan, 09/10/2026). */
    day: {
      label: "Dag",
      previous: "Vorige dag",
      next: "Volgende dag",
      today: "Vandaag",
      past: (date: string) =>
        `Bestellingen van ${date}: bekijken en bonnen afdrukken. Verwijderen kan enkel de eigenaar via ⚙; een afgesloten Z wordt dan herrekend.`,
      loading: "De bestellingen van die dag worden opgehaald…",
    },
    adminNote:
      "Dezelfde lijst als bij Bestellingen (live gesynchroniseerd). Hier kan de eigenaar een foutief ingegeven bestelling rechtzetten; ze blijft zichtbaar als geannuleerd.",
    correctAllowed:
      "Een foutieve bestelling rechtzetten (enkel eigenaar, met code); ze blijft zichtbaar als geannuleerd",
    correctRefused: "Enkel onbetaalde of contant betaalde bestellingen van de zaak kunnen geannuleerd worden",
    correctTitle: (code: string) => `Bestelling ${code} annuleren`,
    // in-app text (b) of the terms, for cancelling instead of deleting (Furkan, 01/10/2026)
    correctOnly: [
      "Annuleer hier enkel een ",
      "foutief ingegeven bestelling",
      ", zoals een dubbele bestelling, een verkeerde tafel of een testbestelling.",
    ],
    correctLimit: [
      "U mag maximaal ",
      "5 bestellingen per dag",
      " op deze manier rechtzetten. De bestelling blijft zichtbaar als geannuleerd, met wie het deed.",
    ],
    correctResponsibility:
      "Uw zaak is als enige verantwoordelijk voor de correcte en volledige registratie en boeking van al haar ontvangsten en voor de gevolgen van elk (mis)bruik; Tafo is daarvoor niet aansprakelijk en uw zaak vrijwaart Tafo.",
    correctReasons: ["Dubbele bestelling", "Verkeerde tafel", "Testbestelling", "Foutief ingegeven"],
    ownerTools: "Meer opties (enkel eigenaar)",
    // a table that was to take away, behind the ⚙ and the special code (Furkan, 15/10/2026)
    toTakeaway: {
      button: "→ Afhaal",
      title: "Deze tafel omzetten naar een afhaling: de btw van een afhaling, ook als al betaald is",
      online: "Een online bestelling kan enkel Tafo aanpassen",
      done: (table: string) => `Tafel ${table} is nu een afhaling, btw aangepast`,
    },
    delete: "Verwijderen",
    deleteAllowed: "Definitief verwijderen (enkel eigenaar, met code)",
    deleteRefused: "Enkel onbetaalde of contant betaalde bestellingen van de zaak kunnen verwijderd worden",
    deleteTitle: (code: string) => `Bestelling ${code} definitief verwijderen`,
    // in-app text (b) of the terms, as approved by the lawyer
    deleteOnly: [
      "Verwijder enkel een ",
      "foutief ingegeven bestelling",
      ", zoals een dubbele bestelling, een verkeerde tafel of een testbestelling.",
    ],
    deleteLimit: [
      "U mag maximaal ",
      "5 bestellingen per dag",
      " verwijderen; een verwijderde bestelling wordt definitief gewist en is niet terug te halen.",
    ],
    deleteResponsibility:
      "Uw zaak is als enige verantwoordelijk voor de correcte en volledige registratie en boeking van al haar ontvangsten en voor de gevolgen van elk (mis)bruik; Tafo is daarvoor niet aansprakelijk en uw zaak vrijwaart Tafo.",
    /** After the Z: the order goes, the Z is recomputed, and the removal is kept with its reason. */
    deleteAfterZ: (number: number) => [
      "Deze bestelling zit in ",
      `Z ${String(number)}`,
      ", die al afgesloten is. De Z wordt herrekend: het archief en het overzicht voor de boekhouder tonen de dag zonder deze bestelling.",
    ],
    deleteAfterZKept:
      "De Z zoals ze afgedrukt werd blijft bewaard. Deze verwijdering komt op de herrekende Z: wie, wanneer, welke bestelling, het bedrag en de reden.",
    deleteReason: "Reden (verplicht)",
    deleteReasonHint: "bv. dubbel ingegeven, testbestelling",
    deleteReasonMissing: "Geef de reden van de verwijdering.",
    deleteConfirm: "Definitief verwijderen",
    /** Several chosen orders in one go: the same warning, the list, and what stays. */
    deleteMany: {
      title: (count: number) =>
        `${String(count)} ${count === 1 ? "bestelling" : "bestellingen"} definitief verwijderen`,
      confirm: (count: number) => `Definitief verwijderen (${String(count)})`,
      listLabel: "Gekozen bestellingen",
      inZ: (number: number) => `in Z ${String(number)}`,
      stays: "blijft staan: met kaart of QR betaald, of een online bestelling",
      overLimit: (count: number) =>
        `Je koos ${String(count)} bestellingen. Volgens de voorwaarden mag je er maximaal 5 per dag verwijderen: jij bent verantwoordelijk voor wat je meer verwijdert.`,
      afterZ: (count: number) =>
        `${String(count)} van deze bestellingen ${count === 1 ? "zit" : "zitten"} in een Z die al afgesloten is. Die Z wordt herrekend: het archief en het overzicht voor de boekhouder tonen de dag zonder ${count === 1 ? "deze bestelling" : "deze bestellingen"}. De reden hieronder geldt voor elk van hen.`,
      progress: (done: number, total: number) => `${String(done)} van ${String(total)} verwijderd…`,
      deleted: (count: number) => `${String(count)} ${count === 1 ? "bestelling" : "bestellingen"} verwijderd`,
      someStayed: (done: number, stayed: number) =>
        `${String(done)} verwijderd, ${String(stayed)} ${stayed === 1 ? "blijft" : "blijven"} staan:`,
      close: "Sluiten",
    },
    deleted: "Verwijderd",
    /** The help under the list (prototype): bold label, then the text. */
    helpSelect: [
      "Selecteren:",
      "klik op een rij voor de bon rechts; Ctrl/⌘-klik of de vakjes voor meerdere, Shift-klik voor een reeks. Met een selectie kan je navigeren naar de adressen of de bonnen in één keer afdrukken.",
    ] as const,
    helpSelectAdmin: [
      "Selecteren:",
      "klik op een rij voor de bon rechts; Ctrl/⌘-klik of de vakjes voor meerdere, Shift-klik voor een reeks. Met een selectie kan je de bonnen in één keer afdrukken.",
    ] as const,
    help: [
      "Bestelling aanpassen:",
      "klik op “Bewerken” of dubbelklik op de rij. Een bestelling die nog niet betaald is, kan iedereen aanpassen; een betaalde bestelling bewerken, de betaalwijze wijzigen of annuleren vraagt een persoonlijke code, zodat vastligt wie het deed. Geannuleerde bestellingen blijven zichtbaar.",
    ] as const,
    // the filters above the list (Furkan, 11/10/2026)
    filters: {
      kind: "Soort",
      method: "Betaalwijze",
      /** A phone: the filters behind one button (Furkan, 19/10/2026). */
      button: "Filter",
      kinds: { all: "Alle", takeaway: "Afhaal", table: "Tafel", delivery: "Levering", online: "Online" },
      methods: { all: "Alle", cash: "Cash", online: "Online", bancontact: "Bancontact", bank_qr: "Bancontact Pro" },
      none: "Geen bestellingen met deze filter.",
      reset: "Filter wissen",
    },
    /** Choosing several orders (prototype routebar / ordbar). */
    selection: {
      bar: "Selectie",
      none: "Niets geselecteerd",
      count: "geselecteerd",
      // "Route starten" made one think of the courier's app; "Bonnen" was unclear (Furkan, 10/10/2026)
      route: (count: number) => `➜ Navigeer${count > 0 ? ` (${String(count)})` : ""}`,
      receipts: (count: number) => `🖨 Afdrukken${count > 1 ? ` (${String(count)})` : ""}`,
      delete: (count: number) => `🗑 Verwijderen${count > 0 ? ` (${String(count)})` : ""}`,
      clear: "selectie wissen",
      routeMax: "Maximum 9 adressen per route: de eerste 9 worden gebruikt.",
      routeMany: "Snelste volgorde wordt automatisch berekend.",
      routeOne: "Navigatie van de zaak naar dit adres.",
      all: "Alles selecteren",
      row: (code: string) => `Selecteer bestelling ${code}`,
      emptyTitle: "Geen bestelling geselecteerd",
      emptyHint:
        "Klik op een bestelling om hier de bon te zien. Ctrl/⌘-klik of de vakjes om er meerdere te kiezen, Shift-klik voor een reeks.",
      /** A phone or tablet has no Ctrl, ⌘ or Shift (Furkan, 18/10/2026). */
      emptyHintTouch: "Tik op een bestelling voor de bon. Meerdere kiezen: tik de vakjes aan.",
      chosen: (count: number) => `${String(count)} bestellingen geselecteerd`,
      total: "Totaal",
      listTotal: (count: number, euro: string) =>
        `${String(count)} ${count === 1 ? "bestelling" : "bestellingen"} · Totaal ${euro}`,
      forRoute: "Leveringen voor route",
      pickOne: "Klik op een bestelling hierboven om enkel die bon te zien.",
    },
    /** "⚠ gewijzigd · was Contant · gewijzigd door Elif 18:14" */
    methodChangedPill: "⚠ gewijzigd",
    methodChangedNote: (was: string, by: string | null, time: string) =>
      `was ${was} · gewijzigd door ${by ?? "de kassa"} ${time}`,
    /** "✎ 18:14 Kaan" */
    editPill: (time: string, by: string | null) => `✎ ${time}${by ? ` ${by}` : ""}`,
    /** A discount given or taken away with an edit: "korting −4", "korting weg". */
    editDiscount: (label: string | null) => (label === null ? "korting weg" : `korting ${label}`),
    withCourier: (name: string) => `bij ${name}`,
    onTheWay: (name: string) => `onderweg · ${name}`,
    byWhom: (name: string) => `door ${name}`,
    /** Betaalwijze wijzigen for several orders (prototype openPayChangeMulti). */
    multiMethod: {
      title: (count: number) => `Betaalwijze wijzigen · ${String(count)} bestellingen`,
      choose: "Nieuwe betaalwijze voor alle geselecteerde bestellingen",
      note: "Een code is niet nodig. Elke wijziging wordt per bestelling bewaard en staat in het Z-rapport.",
      done: (changed: number, to: string, skipped: { same: number; unpaid: number; locked: number }) =>
        [
          `${String(changed)} bestelling${changed === 1 ? "" : "en"} → ${to}`,
          skipped.same > 0 ? `${String(skipped.same)} stond al op ${to}` : "",
          skipped.unpaid > 0 ? `${String(skipped.unpaid)} nog niet betaald` : "",
          skipped.locked > 0 ? `${String(skipped.locked)} niet meer te wijzigen` : "",
          changed > 0 ? "melding naar eigenaar" : "",
        ]
          .filter(Boolean)
          .join(" · "),
    },
    /** ➜ Route starten (prototype openRoute). */
    route: {
      title: (count: number) => (count > 1 ? `Route · ${String(count)} leveringen` : "Route"),
      start: "Start:",
      qr: "Koerier scant = route op gsm vanaf zijn eigen locatie",
      qrLabel: "QR-code met de route voor de gsm van de koerier",
      open: "Navigatie openen in Google Maps",
      give: (name: string) => `Meegeven aan ${name}`,
      given: (count: number, name: string) => `${String(count)} levering(en) meegegeven aan ${name}`,
      noteMany:
        "De volgorde is automatisch de snelste vanaf de zaak. Op deze kassa start de route aan de zaak; op de gsm van de koerier vanaf zijn huidige locatie.",
      noteRough:
        "Nog niet van elke straat is de ligging gekend: de adressen staan van dichtbij naar ver. Op deze kassa start de route aan de zaak; op de gsm van de koerier vanaf zijn huidige locatie.",
      noteOne: "Op deze kassa start de navigatie aan de zaak; op de gsm van de koerier vanaf zijn huidige locatie.",
    },
    /** ✉ in the row and "Mails aan klant" (prototype openMails). */
    mails: {
      button: (count: number) => `✉ ${String(count)}`,
      buttonLabel: (count: number) => `${String(count)} mail(s) aan de klant`,
      title: "Mails aan klant",
      sent: (time: string, from: string, to: string) => `${time} · van ${from} · aan ${to}`,
      waiting: "wacht om te vertrekken",
      failed: (error: string) => `nog niet verstuurd: ${error}`,
      /** A mail has two versions; the customer's mail app shows the one of its theme. */
      version: "Versie van de mail",
      versions: { light: "Witte versie", dark: "Donkere versie" },
      note: "Automatisch verstuurd door Tafo bij telefonische bestellingen als de klant een e-mailadres heeft. Geen reclame, enkel info over deze bestelling, en de stand van zijn spaarkaart na een bestelling met spaarpunten. De klant ziet de witte of de donkere versie, naargelang zijn mail-app. Antwoorden kan hij niet: voor vragen staat het telefoonnummer van de zaak in de mail.",
      sentTo: (address: string) => `✉ Mail naar klant: ${address}`,
    },
    /** 🖨 Afdrukken for several orders. */
    receipts: {
      title: (count: number) => `Afdrukken · ${String(count)} bonnen`,
      // a shop without printers in Beheer › Printers & toestellen prints from the browser
      note: "Er is nog geen kassaprinter ingesteld (Beheer › Printers & toestellen): de bonnen gaan via de browser, elke bon op een eigen blad.",
    },
    helpDelete: "Een foutief ingegeven bestelling rechtzetten kan de eigenaar in Beheer › Bestellingen.",
    helpDeleteAdmin:
      "Rechtzetten kan enkel de eigenaar (met zijn code) en enkel bij onbetaalde of contant betaalde bestellingen; de bestelling blijft zichtbaar als geannuleerd. Kaart- en online betalingen kan niemand annuleren.",
    methodChanged: (from: string, to: string) => `Betaalwijze gewijzigd: ${from} → ${to}`,
    noPayments: "Deze bestelling is nog niet betaald.",
    history: "Geschiedenis",
    table: (table: string) => `tafel ${table}`,
    events: {
      created: "aangemaakt",
      payment_added: "betaling",
      cancelled: "geannuleerd",
      edited: "bewerkt",
      payment_method_changed: "betaalwijze gewijzigd",
      moved: "tafel verplaatst",
      assigned: "koerier",
      departed: "onderweg",
      delivered: "geleverd",
      settled: "afgerekend met koerier",
      split: "deel betaald",
      problem: "probleem onderweg",
      unsettled: "afrekening koerier ongedaan",
      courier_changed: "andere koerier",
      to_takeaway: "klant haalt zelf af",
    },
    /** "Klant haalt zelf af": the delivery cost that went off. */
    deliveryOff: (amount: string) => `leveringskost − ${amount}`,
    // a delivery the customer comes to fetch himself (Furkan, 11/10/2026)
    pickup: {
      button: "Klant haalt zelf af",
      confirm: (code: string, cost: string | null) =>
        `Levering ${code} wordt een afhaling.${cost === null ? "" : ` De leveringskost (${cost}) valt weg.`} Een koerier laat ze los. Daarna reken je af zoals bij een afhaling.`,
      done: (code: string) => `${code} is nu een afhaling`,
    },
    /** The history of "assigned": "koerier · Mehmet" or "koerier · terug naar te leveren". */
    assignedTo: (name: string | null) => name ?? "terug naar te leveren",
  },
  /** "Volg je bestelling" for the customer (prototype openTrack), behind the link in the mail. */
  /** The download page of Tafo for Windows, at tafo.be/install. */
  install: {
    title: "Tafo voor Windows downloaden",
    toTill: "Naar de kassa →",
    eyebrow: "Tafo voor Windows",
    heading: "Je kassa, klaar in een paar minuten.",
    lead: "Installeer Tafo op je kassa-pc: een groot kassascherm met sneltoetsen, je bonprinters en je kassalade. Eén keer installeren, daarna werkt Tafo zichzelf bij.",
    download: "Download voor Windows",
    meta: (version: string, size: string) => `Versie ${version} · ${size} · Windows 10 en 11`,
    notWindows:
      "Je kijkt nu niet op een Windows-pc. Downloaden kan, maar installeren doe je op de kassa-pc zelf: open daar tafo.be/install.",
    none: {
      title: "Er staat nog geen versie klaar",
      text: "De download komt hier zodra Tafo ze vrijgeeft. Probeer het straks opnieuw. Ondertussen werkt de kassa ook in je browser.",
    },
    unreachable: {
      title: "De download is even niet bereikbaar",
      text: "Controleer je internetverbinding en laad de pagina opnieuw.",
      tryAnyway: "Toch proberen te downloaden",
    },
    stepsLabel: "Zo installeer je Tafo",
    steps: [
      {
        title: "Download",
        text: "Klik op de rode knop. Het installatieprogramma (Tafo-Setup) komt in je map Downloads.",
      },
      {
        title: "Installeer",
        text: "Open het bestand. Zegt Windows “Windows heeft uw pc beschermd”? Klik dan op “Meer informatie” en daarna op “Toch uitvoeren”.",
      },
      {
        title: "Koppel je kassa",
        text: "Tafo start vanzelf. Typ bij “Kassa koppelen” de koppelcode van je zaak, en je kan bestellingen opnemen.",
      },
    ],
    facts: [
      {
        title: "Altijd de nieuwste versie",
        text: "Tafo kijkt bij het opstarten of er een nieuwe versie is en installeert ze zelf. Je hoeft hier niet meer terug te komen.",
      },
      {
        title: "Printers en kassalade",
        text: "Tafo vindt je bonprinters in het netwerk en in Windows, en stuurt elke bon naar de juiste printer.",
      },
      {
        title: "Geen Windows?",
        text: "De kassa werkt ook in de browser op app.tafo.be. Voor obers en koeriers is er de Tafo-app op app.tafo.be/app.",
      },
    ],
    /** The drawing of a till next to the button: a few tiles and a ticket, decoration only. */
    tiles: ["Dürüm", "Pita", "Pizza", "Schotels", "Snacks", "Dranken"],
    ticket: [
      ["1× Dürüm kip", "€ 8,50"],
      ["1× Pizza margherita", "€ 10,00"],
      ["2× Cola", "€ 5,00"],
    ] as const,
    ticketTotal: ["Totaal", "€ 23,50"] as const,
    ticketPay: "Afrekenen",
    foot: (year: number) => `© ${String(year)} Tafo · tafo.be`,
  },
  /** "Folder maken" (ADR 0050): the tab in Beheer › Menu and the page /folder/… that makes the PDF. */
  folder: {
    title: "Folder",
    intro: "Maak met één knop een folder om te laten drukken: A3, gevouwen tot A4, met je menu en de prijzen van nu.",
    make: "Folder maken",
    notAvailable: "De folder is voor deze zaak nog niet beschikbaar. Tafo zet hem klaar met jullie eigen drukwerk.",
    ownPrint: "Jullie eigen drukwerk als folder, met de prijzen van nu uit het menu. Klaar om te laten drukken.",
    loading: "De instellingen van de folder worden opgehaald…",
    making: "De folder wordt gemaakt…",
    opened: "De folder staat in een nieuw venster. Kies daar bij Bestemming ‘Opslaan als PDF’.",
    openLink: "Opent er niets? Klik dan hier om de folder te openen.",
    fromElsewhere: "Naam, adres, telefoon en openingsuren komen uit Algemeen, de leveringszones uit Leveringszones.",
    look: "Wat staat erop",
    accent: "Kleur van de kaders en titels",
    accentCustom: "Eigen kleur",
    accents: {
      "#f2b8b5": "Roze",
      "#f5c542": "Geel",
      "#f39a4a": "Oranje",
      "#e8504a": "Rood",
      "#8fd19e": "Groen",
      "#8ec5ff": "Blauw",
      "#f5f1ee": "Wit",
    } as Record<string, string>,
    tagline: "Regel onder de naam",
    taglinePlaceholder: "Bv. Pizza - Pita - Pasta - Snack",
    taglineNote: "Leeg: de namen van de eerste vier categorieën.",
    website: "Website",
    websitePlaceholder: "www.jouwzaak.be",
    orderUrl: "Link van de QR-code",
    orderUrlPlaceholder: "https://www.jouwzaak.be",
    orderUrlNote: "Waar klanten online bestellen. Leeg: geen QR-code op de folder.",
    orderUrlWrong: "Vul een volledige link in, die begint met https://",
    note: "Eigen actie (mag leeg blijven)",
    notePlaceholder: "Bv. Nieuw: verse pasta's",
    noteNote: "Komt onderaan de voorkant, in een kader.",
    zones: "Leveringszones op de voorkant",
    categories: "Categorieën op de folder",
    categoriesNote: "Tik om een categorie weg te laten of terug te zetten. Acties en deals staan er standaard niet op.",
    off: "niet op de folder",
    pictures: "Foto's",
    logo: "Logo",
    photo: "Foto onderaan de voorkant",
    pictureSet: "ingesteld",
    pictureNone: "geen",
    choose: "Kiezen",
    replace: "Vervangen",
    remove: "Weghalen",
    pictureNote: "PNG of JPG, hoogstens 6 MB. Een logo op een witte of doorzichtige achtergrond staat het mooist.",
    tooLarge: "Deze foto is te groot: kies er een van hoogstens 6 MB.",
    // the page /folder/…
    documentTitle: (shop: string) => `Folder ${shop}`,
    howToSave:
      "Kies bij Bestemming ‘Opslaan als PDF’ en klik op Opslaan. Werkt dat niet goed, open deze pagina dan in Chrome of Edge.",
    size: (percent: number) => `Formaat 426 × 303 mm (A3 met 3 mm afloop), tekst op ${String(percent)} %.`,
    tooLong: "Het menu is te lang voor één folder: niet alles past. Laat in Beheer › Menu › Folder een categorie weg.",
    save: "PDF opslaan",
    printSize: (width: number, height: number) =>
      `Het eigen drukwerk van de zaak, ${String(width)} × ${String(height)} mm, met de prijzen van nu.`,
    gone: "Deze link naar de folder werkt niet meer. Maak de folder opnieuw in Beheer › Menu › Folder.",
    // "Bewerken" on the page (Furkan, 21/10/2026)
    edit: "Bewerken",
    editDone: "Klaar",
    editHint:
      "Klik op een tekst of prijs en typ. Enter = bewaren, Esc = annuleren. Verandert een prijs later in het menu, dan neemt de folder het menu weer over.",
    editedCount: (count: number) => (count === 1 ? "1 tekst aangepast." : `${String(count)} teksten aangepast.`),
    resetTexts: "Alles zoals het menu",
    resetConfirm: "Alle aangepaste teksten van de folder terugzetten zoals het menu?",
    saveFailed: "Je wijziging is niet bewaard: de link is verlopen of er is geen verbinding. Open de folder opnieuw.",
  },
  /** "🖨 QR-kaartjes": a card per table with its QR code, to print (Beheer › Tafelplan, Tafo-beheer). */
  tableCards: {
    button: "🖨 QR-kaartjes",
    title: (count: number) => `QR-kaartjes · ${String(count)} tafels`,
    note: "Vier kaartjes op een A4: afdrukken, knippen en op elke tafel zetten. Elke tafel heeft haar eigen code.",
    table: (table: string) => `Tafel ${table}`,
    steps: "Scan · kies · bestel",
    pay: "Betalen bij het personeel",
    print: "Afdrukken",
    close: "Sluiten",
  },
  /** "Eerst goedkeuren aan de kassa": the window of a guest's order that waits for the till. */
  tableQrRequests: {
    title: (table: string) => `QR-bestelling · Tafel ${table}`,
    from: (name: string, time: string) => `${name} · ${time}`,
    more: (count: number) =>
      count === 1 ? "nog 1 QR-bestelling wacht" : `nog ${String(count)} QR-bestellingen wachten`,
    total: "Totaal",
    accept: "Goedkeuren",
    refuse: "Weigeren",
    accepted: (table: string) => `Tafel ${table}: goedgekeurd, naar de keuken`,
    refused: (table: string) => `Tafel ${table}: geweigerd`,
  },
  /** QR-bestellen aan tafel: the guest's page behind the card on the table (/tafel/<token>). */
  tableOrder: {
    title: "Bestellen aan tafel",
    table: (table: string) => `Tafel ${table}`,
    loading: "Menu wordt geladen…",
    gone: "Deze QR-code hoort bij geen tafel (meer). Vraag het personeel.",
    off: "Bestellen met de QR-code kan hier nu niet. Vraag het personeel.",
    closed: (from: string | null) =>
      from === null
        ? "De zaak is nu gesloten: bestellen met de QR-code kan tijdens de openingsuren."
        : `De zaak is nu gesloten: bestellen kan vanaf ${from}.`,
    intro: "Kies wat je wilt, kijk je bestelling na en stuur ze door: ze gaat meteen naar de keuken.",
    cart: (count: number, amount: string) => `Mijn bestelling · ${String(count)} · ${amount}`,
    cartTitle: "Mijn bestelling",
    empty: "Je hebt nog niets gekozen.",
    more: "＋ Nog iets toevoegen",
    total: "Totaal",
    send: "Bestelling versturen",
    sending: "Bezig met versturen…",
    payLater: "Betalen doe je zoals altijd bij het personeel.",
    sentTitle: "✓ Doorgestuurd naar de keuken",
    sent: (table: string, amount: string) =>
      `Je bestelling voor tafel ${table} (${amount}) wordt klaargemaakt. Betalen doe je bij het personeel.`,
    again: "Nog iets bestellen",
    nameLabel: "Je naam",
    namePlaceholder: "Voor het personeel",
    nameNeeded: "Vul je naam in: zo weet het personeel voor wie het is.",
    waitingTitle: "Wacht op bevestiging…",
    waiting: (table: string, amount: string) =>
      `De zaak bekijkt je bestelling voor tafel ${table} (${amount}). Zodra ze bevestigd is, gaat ze naar de keuken.`,
    refusedTitle: "Niet doorgestuurd",
    refused: "De zaak heeft je bestelling niet aanvaard. Vraag het aan het personeel.",
    expired: "De zaak heeft je bestelling niet op tijd bevestigd. Vraag het aan het personeel.",
    added: (name: string) => `${name} toegevoegd`,
    retry: "Opnieuw proberen",
  },
  track: {
    title: "Volg je bestelling",
    order: (code: string) => `Bestelling ${code}`,
    readyTakeaway: (time: string) => `klaar om af te halen rond ${time}`,
    readyDelivery: (time: string) => `levering rond ${time}`,
    preparing: "in voorbereiding",
    delivered: "● geleverd",
    pickedUp: "● afgehaald",
    /** On the way (prototype openTrack): "aankomst ± 20:05 · nog 12 min". */
    arrival: ["aankomst ± ", (minutes: number) => ` · nog ${String(minutes)} min`] as const,
    cancelled: "geannuleerd",
    paid: "Betaald ✓",
    dueDoor: (amount: string, bankQr: boolean) =>
      `Te betalen aan de deur: ${amount} (${bankQr ? "contant of bank-QR" : "contant"})`,
    dueCounter: (amount: string) => `Te betalen bij het afhalen: ${amount}`,
    total: (amount: string) => `Totaal ${amount}`,
    question: (phone: string) => `Vragen? Bel ${phone}.`,
    gone: "Deze link werkt niet meer. Heb je een vraag over je bestelling, bel dan de zaak.",
    by: "mogelijk gemaakt door",
    shop: "zaak",
    home: "jij",
    map: "Kaart met koerier",
    /** The courier's phone tells where he is: the dot and the arrival follow him, not an estimate. */
    live: "● live",
  },
  /** /nieuwsbrief/<token>: "uitschrijven" in the mail about the Dagpromo (Furkan, 14/10/2026). */
  newsletter: {
    title: "Acties per mail",
    subscribed: "Je krijgt de acties van deze zaak per mail.",
    unsubscribed: "Je bent uitgeschreven: je krijgt geen acties meer per mail.",
    stop: "Uitschrijven",
    resume: "Toch weer acties per mail krijgen",
    failed: "Dat lukte niet. Probeer het zo meteen opnieuw.",
    gone: "Deze link werkt niet. Heb je een vraag, bel dan de zaak.",
  },
  /** /nieuwsbrief/bevestigen/<token>: the customer says yes to the newsletter staff switched on (Furkan, 30/10/2026). */
  newsletterConfirm: {
    title: "Nieuwsbrief bevestigen",
    ask: "Wil je de acties van deze zaak per mail krijgen? Bevestig het hieronder.",
    confirm: "Ja, ik wil de nieuwsbrief",
    nothing: "Wil je ze niet? Dan hoef je niets te doen.",
    confirmed: "Bedankt! Je krijgt voortaan de acties van deze zaak per mail.",
    stop: "Uitschrijven kan altijd, met de link onderaan elke mail.",
    failed: "Dat lukte niet. Probeer het zo meteen opnieuw.",
    gone: "Deze link werkt niet meer. Heb je een vraag, bel dan de zaak.",
  },
  editing: {
    bar: (code: string) => `Bestelling ${code} bewerken`,
    /** The bar above the ticket (prototype): "✎ Bestelling X3K bewerken · was € 24,10". */
    barWas: ["✎ Bestelling", (amount: string) => `bewerken · was ${amount}`] as const,
    opened: (code: string) => `Bestelling ${code} geopend om te bewerken`,
    stop: "Stoppen",
    stopped: "Bewerken gestopt, niets gewijzigd",
    nothing: "Niets gewijzigd",
    noLines: "Geen producten meer. Gebruik Annuleren in Bestellingen.",
    finishFirst: "Eerst opslaan of stoppen met bewerken",
    save: "Opslaan",
    saved: (code: string, total: string) => `Bestelling ${code} bijgewerkt · totaal ${total}`,
    courierCollects: (amount: string) => `Koerier int nu ${amount}`,
    giveBack: (amount: string) => `Geef ${amount} terug aan de klant`,
    /** "Korting" next to Opslaan while editing; with the one that is on: "Korting −4". */
    discount: (label: string | null) => (label === null ? "Korting" : `Korting ${label}`),
    discountTitle: "Korting op deze bestelling",
    discountPaid: "Na Opslaan zie je wat de klant terugkrijgt of bijbetaalt.",
    settle: {
      payTitle: "Bijbetalen",
      refundTitle: "Terug te geven",
      wasBecomes: ["Bestelling", (was: string, becomes: string) => `was ${was} en wordt ${becomes}.`] as const,
      due: "Nog te betalen",
      giveBack: "Terug te geven aan klant",
      added: (items: string) => `Bijbesteld: ${items}`,
      removed: (items: string) => `Geschrapt: ${items}`,
      discount: (label: string) => `Korting: ${label}`,
      discountOff: "Korting weggenomen",
      refundWith: { cash: "Contant teruggeven", terminal: "Terugstorten via Bancontact" },
    },
  },
  receipt: {
    kind: "Soort bestelling",
    number: "Bonnummer",
    date: "Datum",
    time: "Tijd",
    method: "Betaalmethode",
    /** As on the receipt of Donass (prototype: "Bancontact / Mister Cash"). */
    methods: { bancontact: "Bancontact / Mister Cash" } as Partial<Record<PaymentMethod, string>>,
    deliveryAddress: "Leveringsadres:",
    salutation: "Dhr. / Mvr.",
    remark: "Opmerking:",
    route: "Route naar dit adres (Google Maps)",
    details: "Details bestelling",
    free: "GRATIS",
    rate: "Tarief",
    base: "Basis",
    vat: "BTW",
    total: "Totaal",
    tip: "Fooi (vrijwillig)",
    paidTotal: "Betaald",
    change: (amount: string) => `Terug: ${amount}`,
    refunded: (amount: string, method: string) => `Teruggegeven: ${amount} (${method})`,
    paid: "REEDS BETAALD!",
    cancelled: "GEANNULEERD",
    payCourier: (amount: string) => `TE BETALEN AAN KOERIER: ${amount}`,
    toPay: (amount: string) => `NOG TE BETALEN: ${amount}`,
    thanks: "Eet smakelijk!",
    tel: (phone: string) => `Tel. ${phone}`,
    /** As JET Connect writes it in the note: the code to give after dialling the platform's number. */
    phoneCode: (code: string) => `Code telefoon ${code}`,
    vatNumber: (number: string) => `BTW ${number}`,
    hours: "Openingsuren",
    pickup: "Afhaaltijd:",
    deliveryTime: "Bezorgtijd:",
    tafo: "Kassa door Tafo · tafo.be",
  },
  paymentMethods: {
    cash: "Contant",
    bancontact: "Bancontact",
    card: "Visa / Mastercard",
    meal_voucher: "Maaltijdcheque",
    bank_qr: "Bancontact Pro QR",
    online: "Online",
  } satisfies Record<PaymentMethod, string>,
  roles: {
    owner: "Eigenaar",
    manager: "Manager",
    staff: "Personeel",
    waiter: "Ober",
    courier: "Koerier",
  } satisfies Record<StaffRole, string>,
};

export type Messages = typeof nl;
